import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { getCurrencySymbol as standardCurrencySymbol } from '@angular/common';
import {
  PageRequest,
  SearchableSelectTypes,
  StateTypes,
} from '../../models/common';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { WizerValidatorService } from 'src/app/utils/wizer.validators.service';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { UtilityService } from 'src/app/utils/utility.service';
import { applyDefaultPhone, guarantorPhoneProblem, phoneDigits } from 'src/app/utils/guarantor-phone';
import { identityVerificationMessage } from 'src/app/utils/identity-verification-message';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { Unsubscribe } from 'src/app/utils/unsubscribe.service';
import { LoanProductService } from 'src/app/pages/settings/services/loan-product.service';
import { NotificationService } from 'src/app/utils/notification.service';
import { LoanService } from 'src/app/pages/loan-management/services/loan.service';
import { GuarantorService } from 'src/app/pages/loan-management/services/guarantor.service';
import { Permissions } from '../../dto/permissions';
import { PermissionService } from 'src/app/utils/permission.service';
import { environment } from 'src/environments/environment';
import { CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input';
import { CurrencyFormatterService } from '../../services/currency-formatter.service';
import { ClientSetupService } from 'src/app/pages/settings/services/client-setup.service';
import { ClientService } from 'src/app/pages/client-management/services/client.service';
import { DataService } from 'src/app/shared/services/data.service';
import {
  LoadingService,
  QuietScope,
} from 'src/app/shared/services/loading.service';
import { CountryService } from 'src/app/pages/settings/services/country.service';
import { CountryAddressService } from 'src/app/shared/services/country-address.service';
import { nationalIdentifierOptions } from 'src/app/utils/national-identifiers';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import { Toast, ToastrService } from 'ngx-toastr';
import {
  VendorDealer,
  VendorDealerDialogComponent,
} from 'src/app/pages/settings/configurations/loan-products/new-loan-product/vendor-dealer-dialog/vendor-dealer-dialog.component';
import { VendorService } from 'src/app/pages/loan-management/services/vendor.service';
import { clampUnit, unitsUpTo } from 'src/app/utils/period-units';

const guarantorTab = {
  name: $localize`:@@guarantor:Guarantor`,
  value: 'guarantor',
  perms: [Permissions.NONE],
};

interface LoanScheduleRow {
  period: number;
  opening: number;
  principal: number;
  interest: number;
  closing: number;
  fees: number;
  savings: number;
  total: number;
}

interface ProductFeeView {
  name: string;
  value: number;
  minimum: number;
  maximum: number;
  event: string;
  eventType?: 'DAILY' | 'QUARTERLY';
  eventTime?: string;
}

@Component({
  selector: 'app-new-loan-account',
  templateUrl: './new-loan-account.component.html',
  styleUrls: ['./new-loan-account.component.css'],
})
export class NewLoanAccountComponent
  extends Unsubscribe
  implements OnInit, AfterViewInit, OnDestroy
{
  /* Both panels start collapsed — the dialog opens on the fields the user has
     to fill, not on the product's reference data. */
  public otherLoanDetailsExpanded = false;
  public calculatorExpanded = false;

  /* The Other Loan Details switches are user-operable. Until one is touched it
     mirrors the product's own configuration; once toggled, the user's choice
     wins. Holding only the overrides (rather than seeding state on product
     load) means a product change is still reflected in any switch the user
     has not overridden. */
  private panelSwitchOverrides = new Map<string, boolean>();

  public isPanelSwitchOn(key: string, productDefault: boolean): boolean {
    const override = this.panelSwitchOverrides.get(key);
    return override === undefined ? !!productDefault : override;
  }

  public togglePanelSwitch(key: string, productDefault: boolean): void {
    this.panelSwitchOverrides.set(
      key,
      !this.isPanelSwitchOn(key, productDefault),
    );
  }

  /* Values for the switch-gated panel fields, same override pattern as the
     switches: the product's value shows until the user types, then theirs
     wins. Deliberately NOT FormControls — onSubmit spreads
     basicForm.getRawValue() straight into the create-loan payload, so adding
     controls here would post field names the API has not been told about.
     Wire these into the request once those names are known. */
  private panelFieldOverrides = new Map<string, string>();

  public panelFieldValue(key: string, productValue: string): string {
    const override = this.panelFieldOverrides.get(key);
    return override === undefined ? (productValue ?? '') : override;
  }

  public setPanelFieldValue(key: string, value: string): void {
    this.panelFieldOverrides.set(key, value);
  }
  public repaymentSchedule: LoanScheduleRow[] = [];
  public scheduleTotals = {
    principal: 0,
    interest: 0,
    fees: 0,
    savings: 0,
    total: 0,
  };
  public CountryISO = CountryISO;
  public PhoneNumberFormat = PhoneNumberFormat;
  public preferredCountries: CountryISO[] = [
    CountryISO.Nigeria,
    CountryISO.UnitedStates,
  ];

  public basicForm: FormGroup;
  public disbursementForm: FormGroup;
  private formValid: boolean[] = [];
  public disableGuarantor: boolean = true;
  public checkingIdentifier: boolean = false;
  public verifyingIdentity: boolean = false;
  public identifierTypeOptions: string[] = ['BVN'];
  public nationalIdentifiers: string[] = ['BVN'];
  public identityVerificationEnabled: boolean = true;
  private guarantorPhoneSearchIdentifierType: string | null = null;
  public isBioDataEditing: boolean = false;
  public hasVerifiedIdentity: boolean = false;
  public foundGuarantorId: number | null = null;
  public identifierRequired: boolean = false;
  public foundGuarantorIdentifierVerified: boolean = false;

  // Standalone search input — keeps phone numbers / raw search text out of the
  // form's bvn field so the bio data display field stays clean.
  public searchInput = new FormControl('');
  public tenantCountryName: string = '';
  private tenantCountryCode: string = '';
  public guarantorForm: FormGroup;
  public loanProducts: SearchableSelectTypes[] = [];
  /** true while a Calculate press is waiting on the schedule */
  public calculating = false;
  /** the request behind the schedule on screen, as sent */
  private calculatedFrom: string | null = null;
  private scheduleRequest: Subscription | null = null;

  private loanProductRecordsList: any[] = [];
  public fundSources = [];
  /** the GL accounts repayments may post to: the product's repaymentGLs */
  public repaymentGLs: any[] = [];

  /* TEMPORARY FIGMA PREVIEW DATA:
     Stands in when a product carries no fund sources, so the Repayment Account
     select is never an empty dropdown. Declared as a constant and assigned
     once — an *ngFor must never iterate a freshly built array, or Angular
     re-diffs the whole option list on every change-detection pass. Remove once
     products reliably carry fund sources. */
  private readonly PREVIEW_FUND_SOURCES = [
    { id: 'GL3000', name: 'GL 3000 - GT Collection Account' },
  ];
  public countries: SearchableSelectTypes[] = [];
  private rawCountries: any[] = [];
  private rawStates: any[] = [];
  private rawBusinessStates: any[] = [];
  private rawLocalGovernments: any[] = [];
  private rawBusinessLocalGovernments: any[] = [];
  public residentialStateLabel: string = 'State';
  public residentialLgaLabel: string = 'Local Govt.';
  public businessStateLabel: string = 'State';
  public businessLgaLabel: string = 'Local Govt.';
  public states: SearchableSelectTypes[] = [];
  public businessStates: SearchableSelectTypes[] = [];
  public localGovernments: SearchableSelectTypes[] = [];
  public businessLocalGovernments: SearchableSelectTypes[] = [];
  private clientId: number;
  public loanProductRecords: any;
  public activeTab: string = 'basic';
  public banks: any[] = [];
  public ownAccount: any[] = [];
  public organizationName: string = '';

  isViewMode: boolean = false;
  disableForm: boolean = false;
  imageValues = {
    photo: '',
    signature: '',
    idCard: '',
  };

  calendarDays = {
    day: {
      week: 7,
      month: 30,
      year: 365,
    },
    week: {
      month: 4,
      year: 52,
    },
    month: {
      year: 12,
    },
  };

  public readonly periodUnits = [
    { label: $localize`:@@dayOption:Day`, value: 'DAY' },
    { label: $localize`:@@weekOption:Week`, value: 'WEEK' },
    { label: $localize`:@@monthOption:Month`, value: 'MONTH' },
    { label: $localize`:@@yearOption:Year`, value: 'YEAR' },
  ];

  /* Interest may also be quoted for the whole tenor, as on the loan product
     form: one rate over the loan rather than per period. Not a length, so it
     stays out of the unit order. */
  public readonly interestRateUnits = [
    ...this.periodUnits,
    { label: $localize`:@@tenorOption:Tenor`, value: 'TENOR' },
  ];

  /* Repayment Frequency offers no unit longer than the Loan Term's, and Grace
     Period none longer than the Repayment Frequency's, as on the loan product
     form (see utils/period-units). Rebuilt when a unit changes, never in a
     getter: an *ngFor must not iterate a fresh array on every pass. */
  public repaymentFrequencyUnits = this.periodUnits;
  public gracePeriodUnits = this.periodUnits;

  /* `clamp` is for a unit the user has just picked (a permission allows it):
     a dependent unit that is now longer than allowed moves down to the
     longest one that is. Filling from a product only filters, so the
     product's own units still show. */
  public refreshUnitOptions(clamp = false): void {
    const term = this.basicForm.get('loanPeriodLengthUnit').value;
    const repayment = this.basicForm.get('loanRepaymentFrequencyPeriodUnit');
    const grace = this.basicForm.get('gracePeriodUnit');

    const repaymentUnit = clampUnit(repayment.value, term);
    if (clamp && repaymentUnit !== repayment.value) repayment.setValue(repaymentUnit);
    this.repaymentFrequencyUnits = unitsUpTo(this.periodUnits, term, repayment.value);

    const graceUnit = clampUnit(grace.value, repayment.value);
    if (clamp && graceUnit !== grace.value) grace.setValue(graceUnit);
    this.gracePeriodUnits = unitsUpTo(this.periodUnits, repayment.value, grace.value);
  }

  selectedBank: any = null;

  public toggleStates: StateTypes[] = [
    {
      name: $localize`:@@basic:Basic`,
      value: 'basic',
      perms: [Permissions.NONE],
      isActive: true,
    },
    {
      name: 'Disbursement Details',
      value: 'disbursementDetails',
      perms: [Permissions.NONE],
      isActive: false,
    },
  ];

  public errorMsg: string = '';
  public isLoadingAccountName: boolean = false;
  /** the latest name enquiry; earlier replies are dropped */
  private nameEnquiryId = 0;
  private accountInputSubject = new Subject<string>();
  public transferTypeOptions: any[];
  // public ownAccountDetails: any[] = [];
  public selectedTransferType: string = 'SAVINGS_ACCOUNT';
  public defaultOwnAccount: any = null;
  public isUsingDefaultAccount: boolean = true;
  public defaultAccountNumber: string = '';
  private static readonly DISBURSEMENT_METHODS = [
    { value: 'DIRECT_TO_VENDOR_DEALER', label: 'Direct to Approved Vendor' },
    { value: 'DIRECT_TO_BORROWER', label: 'Direct to Borrower' },
  ];
  /** reordered per product - see applyProductDisbursementMethod */
  public disbursementMethods = [...NewLoanAccountComponent.DISBURSEMENT_METHODS];
  public vendorSearchControl = new FormControl('');
  /** search box for the third-party bank list, read by the filterOptions pipe */
  public readonly bankSearch = new FormControl('');
  /* Starts with no vendor: the user picks one, and that pick fills the account
     details. A preset id silently chose whichever real vendor had it. */
  public selectedVendorId: number | null = null;
  /* Approved vendors/dealers to disburse an asset financing loan to. Loaded
     from the vendor endpoint in ngOnInit; the Vendors tab under Loans is where
     they are maintained. */
  public vendorDealers: VendorDealer[] = [];
  /** the Third Party fields hold the vendor's account (or wait empty for one), not the borrower's */
  private vendorAccountFilled = false;
  /** the transfer type in use before paying a vendor moved it to Third Party */
  private transferTypeBeforeVendor: string | null = null;

  // Tenant-driven disbursement gating (loaded from the account service in ngOnInit).
  // - isMfb: tenant is a microfinance bank (institution code matches the bank list).
  //   MFIs (isMfb === false) are not required to disburse to a savings account.
  // - tenantAllowsThirdPartyTransfer: the "Transfers To Third Parties" toggle.
  // - tenantHasPaymentIntegration: tenant has an active payment gateway integration.
  // Tenant profile (tenant-and-country): name, ID types, country, identity
  // verification. Passed in by older callers, otherwise fetched on open.
  private organization: any = null;
  private quietScope: QuietScope;

  public isMfb: boolean = false;
  public tenantAllowsThirdPartyTransfer: boolean = false;
  public tenantHasPaymentIntegration: boolean = false;
  // Until the flags arrive we fail open (tab shown, third-party hidden) so an MFB
  // never loses its disbursement tab during the fetch.
  private tenantFlagsLoaded: boolean = false;

  // Corporate client mode (accountType === 'corporate_current')
  public isCorporate: boolean = false;
  public corporateAccounts: any[] = [];
  public selectedCorporateAccountId: any = null;

  public guarantorTab = {
    name: $localize`:@@guarantor:Guarantor`,
    value: 'guarantor',
    perms: [Permissions.NONE],
  };
  constructor(
    private fb: FormBuilder,
    private validatorService: WizerValidatorService,
    private notification: NotificationService,
    private loanProductService: LoanProductService,
    private loanAccountService: LoanService,
    private utils: UtilityService,
    private guarantorService: GuarantorService,
    private permissionService: PermissionService,
    private dialogRef: MatDialogRef<NewLoanAccountComponent>,
    public currencyFormatter: CurrencyFormatterService,
    private toastr: NotificationService,
    private clientSetup: ClientSetupService,
    private clientService: ClientService,
    private countryService: CountryService,
    private countryAddress: CountryAddressService,
    private dialog: MatDialog,
    private vendorService: VendorService,
    private dataService: DataService,
    loading: LoadingService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    super();
    /* The form is usable as soon as it opens, so its start-up requests (and
       the follow-ups they trigger, e.g. the tenant's country, then its
       states) run without the full-screen loader, which used to cover the
       form until every list had arrived. */
    this.quietScope = loading.beginQuietScope();
    this.clientId = data.id;
    /* Seeded from the dialog data so the field is not empty on open, then
       replaced in ngOnInit by the tenant's country bank list - the same source
       client creation uses, so the two screens cannot drift apart. */
    this.banks = data.banks ?? [];
    /* The client pages no longer wait for these before opening the modal;
       anything not passed in is fetched in the background in ngOnInit. */
    this.ownAccount = data.savingsAccount?.data ?? [];
    this.organization = data.organization ?? null;
    // this.ownAccountDetails = data.clientData.externalBankDetails;
    // States are loaded dynamically when a country is selected — do not pre-load here

    this.defaultOwnAccount = data.clientData?.externalBankDetails?.[0] || null;

    // Detect corporate client mode

    if (data.clientData?.accountType) {
      this.isCorporate = data.clientData?.accountType
        .toLowerCase()
        .includes('corporate');
    }
    if (this.isCorporate) {
      this.corporateAccounts = data.clientData?.accounts || [];
    }

    // console.log('Default Own Account:', this.defaultOwnAccount);

    // Set organization name from injected data
    this.organizationName =
      data?.organization?.tenant?.name || 'Savings Account';

    // Transfer type options are tenant driven — "Third Party Account" is only offered
    // when the tenant allows third-party transfers AND has a payment integration.
    // Built here with the current (default) flags and refreshed once the tenant flags
    // load in ngOnInit.
    this.buildTransferTypeOptions();
  }

  /* Transfer types shown by the Figma disbursement flow. Tenant credits the
     borrower's savings account held with the tenant, which only a
     microfinance bank keeps (isMfb, from tenant-and-gateway) - for any other
     tenant it is not offered at all. Until the flags have loaded both are
     listed; once they say the tenant is not an MFB, Tenant drops out and the
     selection moves to Third Party (reconcileSelectedTransferType). */
  private buildTransferTypeOptions(): void {
    const options: any[] = [
      ...(!this.tenantFlagsLoaded || this.isMfb
        ? [{ label: $localize`:@@tenant:Tenant`, value: 'SAVINGS_ACCOUNT' }]
        : []),
      // Third Party is only a real option when the tenant allows it - offering it
      // otherwise defaults the form to a transfer type whose bank/account fields
      // are required but have no tab to fill them in (see shouldShowDisbursementTab).
      ...(!this.tenantFlagsLoaded || this.tenantAllowsThirdPartyTransfer
        ? [
            {
              label: $localize`:@@third_party_account:Third Party Account`,
              value: 'THIRD_PARTY',
            },
          ]
        : []),
    ];

    this.transferTypeOptions = options;
    this.reconcileSelectedTransferType(options);
  }

  /**
   * Keep the selected transfer type valid when the option set changes, so the form
   * doesn't stay stuck on an option that is no longer offered (e.g. an MFI whose only
   * option is Third Party). Setting the value drives onTransferTypeChange.
   */
  private reconcileSelectedTransferType(options: any[]): void {
    if (!this.disbursementForm || !options.length) return;
    const control = this.disbursementForm.get('transferType');
    const stillValid = options.some((o) => o.value === control?.value);
    if (!stillValid) {
      control.setValue(options[0].value);
    }
  }

  /**
   * The disbursement tab is hidden only for a microfinance institution (not an MFB)
   * when third-party transfers are turned off — there is nothing for them to select
   * and disbursement proceeds without a target. Everyone else keeps the tab.
   * Fails open (tab shown) until the tenant flags have loaded.
   */
  private shouldShowDisbursementTab(): boolean {
    if (!this.tenantFlagsLoaded) return true;
    return this.tenantAllowsThirdPartyTransfer || this.isMfb;
  }

  /** MFBs must credit a savings account; MFIs may disburse without one. */
  private isSavingsAccountRequired(): boolean {
    return this.isMfb;
  }

  /**
   * When the disbursement tab is hidden, the Basic tab's primary button is the final
   * action and disburses directly — unless a guarantor step still comes first.
   */
  public get isDirectDisburseFromBasic(): boolean {
    return (
      !this.loanProductRecords?.requireGuarantor &&
      !this.shouldShowDisbursementTab()
    );
  }

  /** When the disbursement tab is hidden, the Guarantor tab's button disburses. */
  public get isDirectDisburseFromGuarantor(): boolean {
    return !this.shouldShowDisbursementTab();
  }

  public get primaryActionLabelBasic(): string {
    return this.isDirectDisburseFromBasic
      ? $localize`:@@submitButton:Submit`
      : $localize`:@@nextButton:Next`;
  }

  public get primaryActionLabelGuarantor(): string {
    return this.isDirectDisburseFromGuarantor
      ? $localize`:@@submitButton:Submit`
      : $localize`:@@nextButton:Next`;
  }

  /**
   * Load the tenant flags that drive disbursement gating, then refresh the transfer
   * type options and the tab list. Fails safe: on error the tab stays visible and
   * third-party stays hidden.
   */
  private loadTenantFlags(): void {
    this.clientSetup
      .getTenantAndGateway()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => {
          const tenant = res?.data?.tenant ?? {};
          this.isMfb = !!tenant.isMfb;
          this.tenantAllowsThirdPartyTransfer =
            !!tenant.enableTransferToThirdParty;
          this.tenantHasPaymentIntegration = !!tenant.hasPaymentIntegration;
          this.tenantFlagsLoaded = true;

          this.buildTransferTypeOptions();
          this.refreshDisbursementTabVisibility();
        },
        error: () => {
          // Leave defaults (tab shown, savings-only, savings optional) on failure.
          this.tenantFlagsLoaded = true;
        },
      });
  }

  /**
   * Reconcile the current tab list with the tenant flags. Removes the disbursement
   * tab when it should be hidden (falling back to the Basic tab if it was active).
   */
  private refreshDisbursementTabVisibility(): void {
    if (this.shouldShowDisbursementTab()) return;

    this.toggleStates = this.toggleStates.filter(
      (state) => state.value !== 'disbursementDetails',
    );

    if (this.activeTab === 'disbursementDetails') {
      this.activeTab = 'basic';
      this.toggleStates.forEach(
        (state) => (state.isActive = state.value === 'basic'),
      );
    }
  }

  close() {
    this.dialogRef.close();
  }

  get dobAgeRange(): Date {
    const date = new Date();
    const yearRange = date.getFullYear() - 18;
    date.setFullYear(yearRange);
    return date;
  }

  // ... your existing properties and constructor ...

  // Add this method
  onBankSelect(bank: any): void {
    this.selectedBank = bank;
  }

  /* The symbol of the loan's currency. The tenant's own keeps the symbol its
     country is configured with; a foreign one takes its standard symbol, the
     same rule as the loan product form. */
  getCurrencySymbol(): string {
    const code = this.loanCurrency;
    if (code === this.currencyFormatter.getCurrencyCode()) {
      return this.currencyFormatter.getCurrencySymbol();
    }
    return standardCurrencySymbol(code, 'narrow');
  }

  /** a figure grouped in thousands with no currency symbol, as the asset row shows them */
  public groupedFigure(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const figure = this.toNumber(value);
    if (figure === 0) return '0';
    return this.currencyFormatter.formatNumber(
      figure,
      Number.isInteger(figure) ? 0 : 2,
    );
  }

  /* An amount as the fields show one: symbol joined to the figure, grouped,
     and decimals only when there are any - "$500", not "$500.00". */
  private moneyText(value: number): string {
    const figure = this.currencyFormatter.formatCurrency(
      value,
      false,
      Number.isInteger(value) ? 0 : 2,
    );
    return figure === '-' ? figure : `${this.getCurrencySymbol()}${figure}`;
  }

  // Get formatted currency for display. formatCurrency would prefix the
  // tenant's symbol, so the loan's own is added here instead.
  // The value may arrive grouped ("20,000" from Loan Size) - parseFloat would
  // read that as 20 - so it is read as a figure first.
  getFormattedCurrency(value: number | string): string {
    if (value === null || value === undefined || value === '') return '-';
    const amount = this.currencyFormatter.formatCurrency(
      this.toNumber(value),
      false,
    );
    return amount === '-' ? amount : `${this.getCurrencySymbol()}${amount}`;
  }

  // Manual trigger for account name enquiry (if you prefer explicit control)
  onAccountDetailsChange() {
    const transferType = this.disbursementForm.get('transferType').value;
    const bankId = this.disbursementForm.get('bankId').value;
    const accountNumber = this.disbursementForm.get('accountNumber').value;

    if (
      transferType === 'THIRD_PARTY' &&
      bankId &&
      accountNumber &&
      accountNumber.length === 10
    ) {
      this.performNameEnquiry(bankId, accountNumber);
    } else {
      this.disbursementForm.get('accountName').setValue('');
      this.errorMsg = '';
    }
  }

  private initForm() {
    const emailPattern =
      '^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\\.[A-Za-z0-9-]+)*\\.[A-Za-z]{2,}$';

    // Basic Form
    this.basicForm = this.fb.group({
      clientId: new FormControl(this.clientId, [Validators.required]),
      productType: new FormControl('LOAN_PRODUCT', Validators.required),
      loanProductId: new FormControl('', Validators.required),
      description: new FormControl(''),
      assetValue: new FormControl({ value: '', disabled: true }),
      insurancePremium: new FormControl(
        { value: '', disabled: true },
        this.rangeValidator('insurancePremium'),
      ),
      // Insurance Premium is now a pair: a type and a value (Figma 15926:26242).
      insurancePremiumType: new FormControl('PERCENTAGE_OF_ASSET_VALUE'),
      totalAssetValue: new FormControl({ value: '', disabled: true }),
      downPaymentPercentage: new FormControl(
        { value: '', disabled: true },
        this.rangeValidator('downPaymentPercentage'),
      ),
      guarantorRelationship: new FormControl(''),
      // The adjustable terms start locked and open once a product has supplied
      // their figures and ranges; see ADJUSTABLE_TERMS.
      principal: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        this.rangeValidator('principal'),
      ]),
      loanPeriodLength: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        this.rangeValidator('loanPeriodLength'),
      ]),
      loanPeriodLengthUnit: new FormControl(
        { value: 'DAY', disabled: true },
        Validators.required,
      ),
      interestRate: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        Validators.pattern(/^(\d{1,2}(\.\d+)?|100(\.0+)?)$/),
        this.rangeValidator('interestRate'),
      ]),
      interestRateUnit: new FormControl(
        { value: 'DAY', disabled: true },
        Validators.required,
      ),
      loanRepaymentFrequency: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        this.rangeValidator('loanRepaymentFrequency'),
      ]),
      loanRepaymentFrequencyPeriodUnit: new FormControl(
        { value: 'DAY', disabled: true },
        Validators.required,
      ),
      amortizationType: new FormControl('', Validators.required),
      fundSourceAccountId: new FormControl({ value: '', disabled: true }),
      gracePeriodLength: new FormControl(
        { value: '', disabled: true },
        this.rangeValidator('gracePeriodLength'),
      ),
      gracePeriodUnit: new FormControl({ value: 'DAY', disabled: true }),
    });

    /* Everything but Type and Product starts locked, so the permissions hold
       from the moment the dialog opens rather than from the first product -
       description and the premium type were otherwise created open. */
    this.lockProductDrivenFields();
    this.refreshUnitOptions();
    this.watchTermRanges();

    // Each of these feeds Total Asset Value and Loan Size (Net), and each can
    // be edited given its permission.
    [
      'assetValue',
      'insurancePremium',
      'insurancePremiumType',
      'downPaymentPercentage',
    ].forEach((name) =>
      this.basicForm
        .get(name)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => this.updateAssetFinancingValues()),
    );

    // Disbursement Form
    this.disbursementForm = this.fb.group({
      disbursementMethod: new FormControl(
        'DIRECT_TO_VENDOR_DEALER',
        Validators.required,
      ),
      transferType: new FormControl('SAVINGS_ACCOUNT', Validators.required),
      savingsAccountId: new FormControl(this.ownAccount?.[0]?.id || ''),
      bankId: new FormControl(''),
      /* The GL account repayments post to. No longer asked for on screen: the
         loan follows its product, and where the product has exactly one
         repayment GL it is still filled in and sent (onLoanProductSelected).
         Not required - with no field, a required control would have kept
         Save disabled for good. */
      repaymentAccountId: new FormControl(''),
      accountName: new FormControl(''),
      accountNumber: new FormControl(''),
    });

    // Listen to transfer type changes
    this.disbursementForm
      .get('transferType')
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((type) => {
        this.onTransferTypeChange(type);
        // Back on Third Party while paying a vendor: the vendor's account, not the client's.
        if (type === 'THIRD_PARTY') this.applyVendorAccount();
      });

    /* Paying a vendor moves to Third Party and the vendor's account; paying
       the borrower again puts back the transfer type and account the borrower
       had, so the loan cannot go to the vendor by accident. */
    this.disbursementForm
      .get('disbursementMethod')
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.isApprovedVendorDisbursement) {
          this.applyVendorAccount();
        } else if (this.transferTypeBeforeVendor) {
          const previous = this.transferTypeBeforeVendor;
          this.transferTypeBeforeVendor = null;
          // The transfer-type handler refills the borrower's details.
          this.disbursementForm.get('transferType').setValue(previous);
        } else if (this.vendorAccountFilled) {
          this.onTransferTypeChange(this.selectedTransferType);
        }
      });

    this.disbursementForm
      .get('accountNumber')
      .valueChanges.pipe(takeUntil(this.unsubscribe$), debounceTime(300))
      .subscribe((accountNumber) => {
        this.onAccountNumberChange(accountNumber);
      });

    // Setup account name enquiry with debounce
    this.setupAccountNameEnquiry();

    this.onLoanProductSelected();
    this.guarantorForm = this.fb.group({
      bvn: new FormControl('', [Validators.maxLength(11)]),
      identifierType: new FormControl('BVN'),
      firstName: new FormControl('', Validators.required),
      middleName: new FormControl(''),
      lastName: new FormControl('', Validators.required),
      gender: new FormControl('', Validators.required),
      mobileNumber: new FormControl('', Validators.required),
      dateOfBirth: new FormControl(''),
      occupation: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.pattern(emailPattern)]),
      countryId: new FormControl(''),
      stateId: new FormControl('', Validators.required),
      lgaId: new FormControl('', Validators.required),
      townName: new FormControl('', Validators.required),
      addressLine1: new FormControl('', Validators.required),
      addressLine2: new FormControl(''),
      businessAddressLine1: new FormControl('', Validators.required),
      businessAddressLine2: new FormControl(''),
      alternatePhoneNumber: new FormControl(''),
      businessCountryId: new FormControl(''),
      businessStateId: new FormControl('', Validators.required),
      businessLgaId: new FormControl('', Validators.required),
      businessTownName: new FormControl('', Validators.required),
      photo: new FormControl('', Validators.required),
      signature: new FormControl('', Validators.required),
      idCard: new FormControl('', Validators.required),
      defaultPhoneSlot: new FormControl('mobileNumber'),
    });

    this.onStateSelected();

    this.guarantorForm
      .get('bvn')
      .valueChanges.pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(600),
        distinctUntilChanged(),
        filter((value) => {
          if (
            !value ||
            !this.foundGuarantorId ||
            this.foundGuarantorIdentifierVerified
          )
            return false;
          const identifierType = (this.guarantorForm.get('identifierType')
            ?.value || 'BVN') as string;
          const minLengthByType: Record<string, number> = { BVN: 11, NIN: 11 };
          const minLength = minLengthByType[identifierType.toUpperCase()] ?? 6;
          return value.length >= minLength;
        }),
      )
      .subscribe((value) => {
        const identifierType =
          this.guarantorForm.get('identifierType')?.value || 'BVN';
        this.verifyingIdentity = true;
        this.verifyIdentityExternally(value, identifierType);
      });
  }
  onAccountNumberChange(accountNumber: string) {
    if (this.selectedTransferType !== 'THIRD_PARTY') return;

    // For corporate, only process when user selected "Others" (manual entry mode)
    if (this.isCorporate && this.selectedCorporateAccountId !== 'others')
      return;

    // Non-corporate: check if account matches the prefilled default
    if (
      !this.isCorporate &&
      this.defaultOwnAccount &&
      accountNumber === this.defaultAccountNumber
    ) {
      this.isUsingDefaultAccount = true;
    } else {
      // User is editing - switch to dropdown mode
      this.isUsingDefaultAccount = false;

      if (accountNumber !== this.defaultAccountNumber) {
        this.disbursementForm.patchValue(
          { accountName: '' },
          { emitEvent: false },
        );
        this.errorMsg = ''; // Clear any previous errors
      }
    }
  }

  getBankNameDisplay(): string {
    // Corporate: derive bank name from selected corporate account
    if (
      this.isCorporate &&
      this.selectedCorporateAccountId &&
      this.selectedCorporateAccountId !== 'others'
    ) {
      const account = this.corporateAccounts.find(
        (a) => a.id === this.selectedCorporateAccountId,
      );
      return account?.bankName || '';
    }
    // Non-corporate: show default account's bank name
    if (this.defaultOwnAccount && this.isUsingDefaultAccount) {
      return this.defaultOwnAccount.bank?.name ?? '';
    }
    return '';
  }

  onCorporateAccountSelected(accountId: any): void {
    this.selectedCorporateAccountId = accountId;
    this.errorMsg = '';

    if (accountId === 'others') {
      // Manual entry mode.

      this.isUsingDefaultAccount = false;
      this.disbursementForm.patchValue({
        bankId: '',
        accountName: '',
        accountNumber: '',
      }); // emitEvent: true (default) — intentional
    } else {
      // Pre-populate from the selected account (bankCode maps to bankId)
      const account = this.corporateAccounts.find((a) => a.id === accountId);

      if (account) {
        this.isUsingDefaultAccount = true;
        this.disbursementForm.patchValue(
          {
            bankId: account.bankCode,
            accountName: account.accountName,
            accountNumber: account.accountNumber,
          },
          { emitEvent: false },
        );
      }
    }
  }

  // onOwnAccountSelected(accountNumber: string) {
  //   const selectedAccount = this.ownAccountDetails.find(
  //     (acc) => acc.accountNumber === accountNumber,
  //   );

  //   if (selectedAccount) {
  //     this.disbursementForm.patchValue(
  //       {
  //         bankId: selectedAccount.bank.id,
  //         accountName: selectedAccount.accountName,
  //         accountNumber: selectedAccount.accountNumber,
  //       },
  //       { emitEvent: false },
  //     );

  //     // Clear any errors
  //     this.errorMsg = '';
  //   }
  // }

  formatExternalBankDisplay(account: any): string {
    return `${account.accountNumber} - ${account.accountName} (${account.bank.name})`;
  }
  // Handle transfer type change
  onTransferTypeChange(type: string) {
    this.selectedTransferType = type;
    this.errorMsg = '';
    // Each branch below refills the fields with the borrower's own details,
    // so a name still being looked up belongs to the old ones.
    this.vendorAccountFilled = false;
    this.nameEnquiryId++;
    this.isLoadingAccountName = false;

    const savingsAccountControl = this.disbursementForm.get('savingsAccountId');
    const bankIdControl = this.disbursementForm.get('bankId');
    const accountNameControl = this.disbursementForm.get('accountName');
    const accountNumberControl = this.disbursementForm.get('accountNumber');

    // Clear all validators first
    savingsAccountControl.clearValidators();
    bankIdControl.clearValidators();
    accountNameControl.clearValidators();
    accountNumberControl.clearValidators();

    if (type === 'SAVINGS_ACCOUNT') {
      // MFBs must credit a savings account; MFIs may disburse without selecting one.
      if (this.isSavingsAccountRequired()) {
        savingsAccountControl.setValidators([Validators.required]);
      }

      // Reset third party fields
      this.disbursementForm.patchValue(
        {
          bankId: '',
          accountName: '',
          accountNumber: '',
        },
        { emitEvent: false },
      );

      /* Switching to Third Party clears this control, so coming back left
         Account to Credit blank - it reads the client's account through
         savingsAccountId. It is not a user choice here, so restore the same
         default the form opened with. */
      if (!savingsAccountControl.value) {
        savingsAccountControl.setValue(this.ownAccount?.[0]?.id || '', {
          emitEvent: false,
        });
      }

      this.isUsingDefaultAccount = false;
    } else if (type === 'THIRD_PARTY') {
      // Set validators for third party account
      bankIdControl.setValidators([Validators.required]);
      accountNameControl.setValidators([Validators.required]);
      accountNumberControl.setValidators([
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(10),
        Validators.pattern(/^\d{10}$/),
      ]);

      // Reset savings account
      savingsAccountControl.reset();

      if (this.isCorporate) {
        // Corporate: auto-select the first account in the list
        const firstAccount = this.corporateAccounts?.[0];
        if (firstAccount) {
          this.selectedCorporateAccountId = firstAccount.id;
          this.isUsingDefaultAccount = true;
          this.disbursementForm.patchValue(
            {
              bankId: firstAccount.bankCode,
              accountName: firstAccount.accountName,
              accountNumber: firstAccount.accountNumber,
            },
            { emitEvent: false },
          );
        } else {
          this.selectedCorporateAccountId = null;
          this.isUsingDefaultAccount = false;
          this.disbursementForm.patchValue(
            { bankId: '', accountName: '', accountNumber: '' },
            { emitEvent: false },
          );
        }
      } else if (this.defaultOwnAccount) {
        // Non-corporate: auto-fill with the client's default external bank account
        // console.log(
        //   'Auto-filling with default own account:',
        //   this.defaultOwnAccount,
        // );

        this.isUsingDefaultAccount = true;
        this.defaultAccountNumber = this.defaultOwnAccount.accountNumber;

        this.disbursementForm.patchValue(
          {
            bankId: this.defaultOwnBankCode,
            accountName: this.defaultOwnAccount.accountName,
            accountNumber: this.defaultOwnAccount.accountNumber,
          },
          { emitEvent: false },
        );
      } else {
        this.isUsingDefaultAccount = false;
      }
    }

    // Update validity for all controls
    savingsAccountControl.updateValueAndValidity();
    bankIdControl.updateValueAndValidity();
    accountNameControl.updateValueAndValidity();
    accountNumberControl.updateValueAndValidity();
  }

  getSelectedOwnAccount() {
    const accountId = this.disbursementForm.get('ownAccountId').value;
    if (!accountId) return null;
    return this.ownAccount.find((acc) => acc.id === accountId);
  }

  // Format savings account display
  formatAccountDisplay(account: any): string {
    return `${account.accountNumber} - ${account.accountName || account.productName}`;
  }

  public get selectedTenantAccount(): any {
    const accountId = this.disbursementForm?.get('savingsAccountId')?.value;
    return this.ownAccount.find((account) => account.id === accountId) || null;
  }

  public get selectedTenantAccountNumber(): string {
    return this.selectedTenantAccount?.accountNumber || '';
  }

  /** the account holder's real name, or '' when none is known */
  private get accountHolderName(): string {
    const account = this.selectedTenantAccount;
    const client = this.data?.clientData || {};
    const name =
      account?.accountName ||
      client?.accountName ||
      client?.name ||
      [client?.firstName, client?.lastName].filter(Boolean).join(' ');
    // A name without a middle name can come back with a double space.
    return String(name || '').replace(/\s+/g, ' ').trim();
  }

  public get tenantAccountName(): string {
    return this.accountHolderName || 'John David';
  }

  /* Client, product and, once the loan has one, its account number - real
     values only, as this is posted with the disbursement. A new loan has no
     number yet, so the reference is left out rather than filled with the
     design's placeholder. */
  public get disbursementNarration(): string {
    return [
      this.accountHolderName,
      this.loanProductRecords?.name,
      this.data?.loanAccountNumber || this.data?.accountNumber,
    ]
      .filter(Boolean)
      .join(' - ');
  }

  public get selectedDisbursementBank(): any {
    const bankId = this.disbursementForm?.get('bankId')?.value;
    const defaultBank = this.defaultOwnAccount?.bank;
    return (
      this.banks?.find(
        (bank) => bank?.code === bankId || bank?.id === bankId,
      ) ||
      (defaultBank?.id === bankId || defaultBank?.code === bankId
        ? defaultBank
        : null)
    );
  }

  public get selectedDisbursementBankName(): string {
    return this.selectedDisbursementBank?.name || '';
  }

  /* The client's saved bank account carries its bank as the client record
     holds it ({ id, name }, sometimes a code), while the Bank Name options are
     keyed by the country-data bankCode - so the record's id matched no option
     and the dropdown stayed blank. Resolve it to an option: by code where the
     record has one, then by name, then by number, since client creation stores
     the code as a number ("000013" saved as 13). '' when nothing matches. */
  private get defaultOwnBankCode(): string {
    const account = this.defaultOwnAccount;
    if (!account || !this.banks?.length) return '';
    const bank = account.bank || {};

    const codes = [bank.code, bank.bankCode, account.bankCode]
      .filter((code) => code !== null && code !== undefined && code !== '')
      .map(String);
    const byCode = this.banks.find((option) => codes.includes(String(option.code)));
    if (byCode) return byCode.code;

    const name = String(bank.name ?? account.bankName ?? '').trim().toLowerCase();
    const byName = name
      ? this.banks.find((option) => String(option.name).trim().toLowerCase() === name)
      : null;
    if (byName) return byName.code;

    const numbers = [...codes, bank.id]
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const byNumber = this.banks.find((option) =>
      numbers.includes(Number(option.code)),
    );
    return byNumber?.code ?? '';
  }

  public get selectedTransferTypeLabel(): string {
    return (
      this.transferTypeOptions?.find(
        (option) => option.value === this.selectedTransferType,
      )?.label || ''
    );
  }

  public get showFirstBankMark(): boolean {
    return /first\s*bank/i.test(this.selectedDisbursementBankName);
  }

  onBankOrAccountChange() {
    const transferType = this.disbursementForm.get('transferType').value;
    const bankId = this.disbursementForm.get('bankName').value;
    const accountNumber = this.disbursementForm.get('accountToCredit').value;

    if (transferType === 'THIRD_PARTY' && bankId && accountNumber) {
      this.performNameEnquiry(bankId, accountNumber);
    } else {
      this.disbursementForm.get('accountName').setValue('');
      this.errorMsg = '';
    }
  }

  private setupAccountNameEnquiry() {
    combineLatest([
      this.disbursementForm
        .get('bankId')
        .valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          startWith(this.disbursementForm.get('bankId').value),
        ),
      this.disbursementForm
        .get('accountNumber')
        .valueChanges.pipe(
          debounceTime(800),
          distinctUntilChanged(),
          startWith(this.disbursementForm.get('accountNumber').value),
        ),
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
        /* The borrower's and the vendor's accounts are filled in without
           emitting, so the streams can hold stale values; they only signal
           an edit, and the enquiry reads what the form holds now. */
        map(() => [
          this.disbursementForm.get('bankId').value,
          this.disbursementForm.get('accountNumber').value,
        ]),
        filter(([bankId, accountNumber]) => {
          const transferType = this.disbursementForm.get('transferType').value;

          // Only proceed if transfer type is THIRD_PARTY
          if (transferType !== 'THIRD_PARTY') {
            // console.log('Not third party, skipping');
            return false;
          }

          // Don't trigger if using a known account (default or selected corporate)
          if (this.isUsingDefaultAccount) {
            // Only restore accountName from defaultOwnAccount when non-corporate
            if (this.defaultOwnAccount) {
              this.disbursementForm
                .get('accountName')
                .setValue(this.defaultOwnAccount.accountName);
            }
            // For corporate the accountName is already set by onCorporateAccountSelected
            return false;
          }

          if (!bankId) {
            // console.log('No bank selected, skipping');
            return false;
          }

          if (!accountNumber || accountNumber.length !== 10) {
            // console.log('Invalid account number, skipping');
            return false;
          }

          if (typeof bankId !== 'string') {
            return;
          }

          // Don't trigger if values match default own account
          if (
            this.defaultOwnAccount &&
            bankId === this.defaultOwnBankCode &&
            accountNumber === this.defaultOwnAccount.accountNumber
          ) {
            // console.log('Matches default account, skipping');
            return false;
          }

          // console.log('All checks passed, triggering name enquiry');
          return true;
        }),
      )
      .subscribe(([bankId, accountNumber]) => {
        // console.log('Performing name enquiry for:', { bankId, accountNumber });
        this.performNameEnquiry(bankId, accountNumber);
      });
  }
  private setupAccountInputDebounce() {
    this.accountInputSubject
      .pipe(debounceTime(800), distinctUntilChanged())
      .subscribe(() => {
        this.onBankOrAccountChange();
      });
  }
  // Perform name enquiry API call
  private performNameEnquiry(bankCode: string, accountNumber: string) {
    this.errorMsg = '';
    this.isLoadingAccountName = true;
    const enquiry = ++this.nameEnquiryId;

    // Clear previous account name
    this.disbursementForm.get('accountName').setValue('');

    this.loanAccountService
      .accountNameEnquiry(bankCode, accountNumber)
      .pipe(
        takeUntil(this.unsubscribe$),
        /* A reply for details the form has since moved off - another vendor,
           or back to the borrower - would put the wrong name on the account. */
        filter(() => enquiry === this.nameEnquiryId),
      )
      .subscribe({
        next: (response: any) => {
          this.isLoadingAccountName = false;
          if (response?.successful && response?.data?.accountName) {
            const accountName = response.data.accountName;
            this.disbursementForm.get('accountName').setValue(accountName);
            this.errorMsg = '';
          } else {
            this.errorMsg =
              'Account name not found. Please verify the account number.';
            this.toastr.info(this.errorMsg);
            this.disbursementForm.get('accountName').setValue('');
          }
        },
        error: (error) => {
          if (enquiry !== this.nameEnquiryId) return;
          this.isLoadingAccountName = false;
          // console.error('Error performing name enquiry:', error);
          this.errorMsg =
            'Failed to retrieve account name. Please check the details and try again.';
          this.disbursementForm.get('accountName').setValue('');
        },
      });
  }

  get basicFormCtrl() {
    return this.basicForm.controls;
  }

  get guarantorsCtrl() {
    return this.guarantorForm.controls;
  }
  getLocalizedState() {
    return $localize`:@@stateLabel:State`;
  }
  getLocalizedLgvt() {
    return $localize`:@@localGovtLabel:Local Govt.`;
  }
  getLocalizedBusinessState() {
    return $localize`:@@stateLabel:State`;
  }
  getLocalizedBusinessLgvt() {
    return $localize`:@@localGovtLabel:Local Govt.`;
  }
  getLocalizedLoanProductsLabel() {
    return $localize`:@@productLabel:Product`;
  }

  // Update the onLoanProductSelected method:

  onLoanProductSelected() {
    this.basicForm.controls['loanProductId'].valueChanges
      .pipe(
        filter((id) => !!id),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
        switchMap((loanProductId) => {
          return this.loanProductService
            .getLoanProduct(loanProductId)
            .pipe(map((record: any) => record.data));
        }),
      )
      .subscribe((data) => {
        this.loanProductRecords = data;
        // Disbursement accounts: the product's disbursementGLs (fundSources
        // on a product saved before the rename).
        const fundSources = data.disbursementGLs?.length
          ? data.disbursementGLs
          : data.fundSources;
        this.fundSources = fundSources?.length
          ? fundSources
          : this.PREVIEW_FUND_SOURCES;

        // Repayment accounts come from the product's repaymentGLs. With several
        // the user picks one (the field is required); with exactly one it is
        // the default.
        this.repaymentGLs = data.repaymentGLs ?? [];
        const onlyRepaymentGL =
          this.repaymentGLs.length === 1 ? this.repaymentGLs[0] : null;
        this.disbursementForm
          .get('repaymentAccountId')
          ?.setValue(onlyRepaymentGL?.glAccountId ?? onlyRepaymentGL?.id ?? '', {
            emitEvent: false,
          });
        const firstSource = this.fundSources[0];

        // The product's fund source is also where the loan is disbursed from.
        this.basicForm
          .get('fundSourceAccountId')
          .setValue(firstSource?.glAccountId ?? firstSource?.id ?? '', {
            emitEvent: false,
          });

        this.toggleStates = [
          {
            name: $localize`:@@basic:Basic`,
            value: 'basic',
            perms: [Permissions.NONE],
            isActive: this.activeTab === 'basic',
          },
        ];

        if (this.loanProductRecords.requireGuarantor) {
          this.toggleStates.push({
            name: $localize`:@@guarantor:Guarantor`,
            value: 'guarantor',
            perms: [Permissions.NONE],
            isActive: this.activeTab === 'guarantor',
          });
        }

        if (this.shouldShowDisbursementTab()) {
          this.toggleStates.push({
            name: 'Disbursement Details',
            value: 'disbursementDetails',
            perms: [Permissions.NONE],
            isActive: this.activeTab === 'disbursementDetails',
          });
        }

        this.basicForm.patchValue({
          loanProductId: data.id,
          // Seeded from the product; the user can replace it with the
          // borrower's own purpose before submitting.
          description: data.description,
          loanPeriodLength: data.defaultLoanPeriodLength,
          // Grouped, as Loan Size shows it once typed in. Asset financing
          // replaces it with the worked-out figure just below.
          principal: this.groupedFigure(data.defaultPrincipal),
          interestRate: data.defaultInterestRate,
          // GET spells these differently from POST; accept either.
          interestRateUnit:
            data.interestRatePeriodUnit ?? data.interestPeriodUnit,
          loanPeriodLengthUnit:
            data.loanPeriodUnit ?? data.loanPeriodLengthUnit,
          guarantorRelationship: '',
          loanRepaymentFrequency: data.loanRepaymentFrequency,
          loanRepaymentFrequencyPeriodUnit:
            data.loanRepaymentFrequencyPeriodUnit,
          amortizationType: data.amortizationType,
          gracePeriodLength: data.gracePeriodLength,
          gracePeriodUnit: data.gracePeriodUnit,
          /* The product's Principal column is the asset price this product is
             configured to finance, so it seeds Asset Value. The net loan is
             then that minus the down payment, which is the relationship the
             API documents (assetValue 5,000,000 -> principal 4,500,000 at 10%).
             Still editable: the actual asset may cost more or less. */
          // Grouped to match what the field shows once typed in.
          assetValue: this.groupedFigure(data.defaultPrincipal),

          /* Both come from the product. Left blank when it does not set them,
             rather than defaulted to a figure nobody configured - a wrong
             down payment quietly changes the amount financed. */
          downPaymentPercentage:
            data.minimumDownPaymentPercent ??
            data.minimumDownPayment ??
            data.minimumDownPaymentPercentage ??
            data.downPaymentPercentage ??
            '',
          insurancePremium: data.enableInsurancePremium
            ? (data.insuranceAnnualPremiumPercent ?? '')
            : '',
          insurancePremiumType: 'PERCENTAGE_OF_ASSET_VALUE',
        });
        this.updateAssetFinancingValues();
        this.applyProductDisbursementMethod(data.disbursementMethod);
        // The product's own terms, calculated once; edits wait for Calculate.
        this.calculateSchedule();
        this.lockProductDrivenFields();
        // Filter only: the product's units show as it set them.
        this.refreshUnitOptions();

        /* Both panels hold nothing but the selected product's terms, so they
           open once there is a product to show. Collapsed by default only
           because before that they would be empty. */
        this.otherLoanDetailsExpanded = true;
        this.calculatorExpanded = true;
      });
  }

  /* Most of this step is defined by the chosen loan product, so it is shown
     rather than edited. Disabled controls are still returned by getRawValue(),
     so the payload is unaffected. Type and Product are always editable; the
     rest opens field by field, see ADJUSTABLE_TERMS. */
  private static readonly EDITABLE_BASIC_FIELDS = [
    'productType',
    'loanProductId',
  ];

  /* The product's terms a loan officer may change for this loan, each opened
     by its own permission. Figures stay within the product's range (checked by
     termsWithinRange). Total Asset Value and Loan Size (Net) are derived, so
     neither is here. */

  private static readonly ADJUSTABLE_TERMS: { [control: string]: Permissions } =
    {
      description: Permissions.EDIT_DESCRIPTION,
      principal: Permissions.MODIFY_LOAN_SIZE,
      interestRate: Permissions.MODIFY_INTEREST_RATE,
      interestRateUnit: Permissions.EDIT_INTEREST_RATE_UNIT,
      loanPeriodLength: Permissions.MODIFY_LOAN_TERM,
      loanPeriodLengthUnit: Permissions.EDIT_LOAN_TERM_UNIT,
      loanRepaymentFrequency: Permissions.MODIFY_REPAYMENT_FREQUENCY,
      loanRepaymentFrequencyPeriodUnit:
        Permissions.EDIT_REPAYMENT_FREQUENCY_UNIT,
      gracePeriodLength: Permissions.MODIFY_GRACE_PERIOD,
      gracePeriodUnit: Permissions.EDIT_GRACE_PERIOD_UNIT,
      assetValue: Permissions.EDIT_ASSET_FINANCE_VALUE,
      insurancePremiumType: Permissions.EDIT_ASSET_FINANCE_INSURANCE_UNIT,
      insurancePremium: Permissions.EDIT_ASSET_FINANCE_INSURANCE_VALUE,
      downPaymentPercentage: Permissions.EDIT_ASSET_FINANCE_DOWN_PAYMENT,
    };

  private static readonly ASSET_FINANCING_TERMS = [
    'assetValue',
    'insurancePremiumType',
    'insurancePremium',
    'downPaymentPercentage',
  ];

  /* Changing the loan type starts the application again: a product of the other
     type shares none of its terms, so anything already on screen belongs to a
     loan that is no longer being applied for. Everything derived from the old
     product goes, and the fields unlock so the next product can fill them. */
  private startOver(): void {
    const clientId = this.basicForm.get('clientId').value;
    const productType = this.basicForm.get('productType').value;

    this.basicForm.reset(
      {
        clientId,
        productType,
        loanProductId: '',
        description: '',
        // The unit selects have no meaning until a product supplies them, but
        // a select with no value renders blank, so they return to their seeds.
        loanPeriodLengthUnit: 'DAY',
        interestRateUnit: 'DAY',
        loanRepaymentFrequencyPeriodUnit: 'DAY',
        gracePeriodUnit: 'DAY',
        insurancePremiumType: 'PERCENTAGE_OF_ASSET_VALUE',
      },
      { emitEvent: false },
    );

    // Product-derived reference data, and any schedule still being fetched.
    this.loanProductRecords = null;
    this.fundSources = [];
    this.repaymentGLs = [];
    this.clearSchedule();

    /* The panels track only what the user has overridden, so clearing the maps
       returns every switch and field to whatever the next product says. */
    this.panelSwitchOverrides.clear();
    this.panelFieldOverrides.clear();
    this.otherLoanDetailsExpanded = false;
    this.calculatorExpanded = false;

    /* Disbursement is tied to the product's fund sources and to the loan
       amount, so it starts again too - on the first transfer type offered:
       Tenant for a microfinance bank, Third Party for any other tenant.
       When neither is offered (MFI, third-party transfers off - no disbursement
       tab at all) fall back to Tenant: unlike Third Party it needs no bank/account
       fields, so the form stays valid with nothing left for the user to fill in. */
    const firstTransferType =
      this.transferTypeOptions?.[0]?.value ?? 'SAVINGS_ACCOUNT';
    this.disbursementForm.reset(
      {
        disbursementMethod: 'DIRECT_TO_VENDOR_DEALER',
        transferType: firstTransferType,
        savingsAccountId: this.ownAccount?.[0]?.id || '',
        bankId: '',
        repaymentAccountId: '',
        accountName: '',
        accountNumber: '',
      },
      { emitEvent: false },
    );
    this.selectedVendorId = null;
    this.transferTypeBeforeVendor = null;
    this.disbursementMethods = [...NewLoanAccountComponent.DISBURSEMENT_METHODS];
    this.vendorSearchControl.reset('', { emitEvent: false });
    /* The reset above is silent, so the screen would stay on the old transfer
       type - Third Party, after paying a vendor - while the form says Tenant. */
    this.onTransferTypeChange(firstTransferType);

    // The guarantor belongs to the application being abandoned.
    this.resetGuarantor();

    /* With no product there is nothing to adjust, so this locks the terms until
       the next product fills them; patchValue fills disabled controls anyway. */
    this.lockProductDrivenFields();
    this.refreshUnitOptions();
    this.activeTab = 'basic';
  }

  private resetGuarantor(): void {
    this.guarantorForm?.reset(
      { identifierType: 'BVN', defaultPhoneSlot: 'mobileNumber' },
      { emitEvent: false },
    );
    this.imageValues = { photo: '', signature: '', idCard: '' };
    this.foundGuarantorId = null;
    this.hasVerifiedIdentity = false;
    this.guarantorPhoneSearchIdentifierType = null;
  }

  private lockProductDrivenFields(): void {
    Object.keys(this.basicForm.controls).forEach((name) => {
      if (NewLoanAccountComponent.EDITABLE_BASIC_FIELDS.includes(name)) return;
      // clientId is hidden and carries the client this loan belongs to.
      if (name === 'clientId') return;
      const control = this.basicForm.get(name);
      if (this.canAdjust(name)) control.enable({ emitEvent: false });
      else control.disable({ emitEvent: false });
    });
    this.lockDisbursementMethod();
  }

  /* On asset financing the Disbursement Method (Disbursement tab) opens only
     to a user holding its permission, once a product is chosen. Disabled, it
     still reaches the payload: that reads .value / getRawValue(), which keep
     a disabled control's value. */
  private lockDisbursementMethod(): void {
    const control = this.disbursementForm?.get('disbursementMethod');
    if (!control) return;
    const locked =
      this.isAssetFinancing &&
      this.lockedWithout(Permissions.EDIT_ASSET_FINANCE_DISBURSEMENT_METHOD);
    if (locked) control.disable({ emitEvent: false });
    else control.enable({ emitEvent: false });
  }

  /* Open only to a user holding the field's permission, and only once a
     product has supplied the figure and its range. Asset financing derives its
     loan size from the asset value, so there principal stays read-only as
     Loan Size (Net). The asset figures exist only on that type, and the
     premium only when the product carries insurance. */
  private canAdjust(control: string): boolean {
    const permission = NewLoanAccountComponent.ADJUSTABLE_TERMS[control];
    const product = this.loanProductRecords;
    if (!permission || !product) return false;
    if (control === 'principal' && this.isAssetFinancing) return false;
    if (NewLoanAccountComponent.ASSET_FINANCING_TERMS.includes(control)) {
      if (!this.isAssetFinancing) return false;
      const isPremium =
        control === 'insurancePremium' || control === 'insurancePremiumType';
      if (isPremium && !product.enableInsurancePremium) return false;
    }
    return this.permissionService.hasPermissionList([permission]);
  }

  /** the product's own settings are read-only on this screen */
  public get panelsLocked(): boolean {
    return true;
  }

  /* The exception is the missed-payment penalty, and the whole of it answers
     to the one permission. Without it, the switch and its fields - basis,
     amount, grace period and unit - show the product's settings read-only.
     With it the switch is the user's, and the fields open while it is on
     (the template pairs this with the switch). Issued as an "Asset Finance"
     permission, but the penalty is the same on both types. */
  public get penaltyLocked(): boolean {
    return this.lockedWithout(Permissions.EDIT_ASSET_FINANCE_PENALTY_SWITCH);
  }

  private static readonly PENALTY_FIELDS = [
    'missedPaymentPenaltyBasis',
    'missedPaymentPenaltyAmount',
    'missedPaymentPenaltyGracePeriod',
    'missedPaymentPenaltyGracePeriodUnit',
  ];

  /** the penalty's basis as shown: the user's choice, else the product's; '' while switched off */
  public get penaltyBasis(): string {
    return this.penaltyFieldValue(
      'missedPaymentPenaltyBasis',
      this.productLatePayment.type,
    );
  }

  /* An amount is not a percentage of the instalment, so switching the basis
     clears the amount to be entered again, in the new basis. */
  public changePenaltyBasis(basis: string): void {
    this.setPanelFieldValue('missedPaymentPenaltyBasis', basis);
    this.setPanelFieldValue('missedPaymentPenaltyAmount', '');
  }

  /* The penalty amount as its field shows it. On a Flat basis the symbol is
     joined to the figure, grouped - "$1,000" - the way the product form's
     amounts read; a separate prefix sat apart from the figure and below its
     line. A percentage stays a bare figure, with its % after the field. */
  public get penaltyAmountText(): string {
    const raw = String(
      this.penaltyFieldValue(
        'missedPaymentPenaltyAmount',
        this.productLatePayment.amount,
      ) ?? '',
    );
    if (!raw || this.penaltyBasis !== 'FLAT') return raw;
    const [whole, fraction] = raw.split('.');
    const separator = this.currencyFormatter.getConfig().thousandSeparator;
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return `${this.getCurrencySymbol()}${grouped}${
      fraction !== undefined ? `.${fraction}` : ''
    }`;
  }

  /* Keeps the figure alone - the symbol and separators are only how it is
     shown - and writes the shown text straight back, so a stray letter is
     dropped at once rather than waiting for the figure itself to change. */
  public onPenaltyAmountInput(input: HTMLInputElement): void {
    const figure = String(input.value ?? '')
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');
    this.setPanelFieldValue('missedPaymentPenaltyAmount', figure);
    input.value = this.penaltyAmountText;
  }

  public get premiumIsFlat(): boolean {
    return this.basicForm?.get('insurancePremiumType')?.value === 'FLAT';
  }

  /* Likewise for the premium: a flat amount and a percentage of the asset
     value are different figures. Bound to the select's selectionChange, so a
     product filling in its own type leaves its premium alone. */
  public clearInsurancePremium(): void {
    this.basicForm.get('insurancePremium').setValue('');
  }

  /* Switching the penalty either way drops what was typed into its fields,
     so switching it back on refills them from the product rather than
     restoring earlier edits. */
  public togglePenalty(): void {
    this.togglePanelSwitch(
      'missedPaymentPenalty',
      this.productLatePayment.required,
    );
    NewLoanAccountComponent.PENALTY_FIELDS.forEach((key) =>
      this.panelFieldOverrides.delete(key),
    );
  }

  /* A switched-off penalty shows its fields empty - it carries no figures,
     and the payload sends none. Switched on: the user's figure, else the
     product's. */
  public penaltyFieldValue(key: string, productValue: any): string {
    return this.isPanelSwitchOn(
      'missedPaymentPenalty',
      this.productLatePayment.required,
    )
      ? this.panelFieldValue(key, productValue)
      : '';
  }

  /** locked until a product is chosen, and after that without the permission */
  private lockedWithout(permission: Permissions): boolean {
    return (
      !this.loanProductRecords ||
      !this.permissionService.hasPermissionList([permission])
    );
  }

  /* One bank list across the app: the tenant's country list from the
     country-data API, the same source client creation reads. The option value
     is the real bankCode, which is what the disbursement payload carries. */
  private loadBanks(): void {
    this.countryAddress
      .getTenantBanksWithCode()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((banks) => {
        if (!banks?.length) return;
        this.banks = banks;
        /* Third Party may already be showing the client's own account; its
           bank could not be matched before the list existed, so fill it now
           - only while the field is still empty and that account is in use. */
        const bankId = this.disbursementForm?.get('bankId');
        if (
          this.selectedTransferType === 'THIRD_PARTY' &&
          this.isUsingDefaultAccount &&
          !this.isCorporate &&
          bankId &&
          !bankId.value
        ) {
          bankId.setValue(this.defaultOwnBankCode, { emitEvent: false });
        }
      });
  }

  public get isAssetFinancing(): boolean {
    return (
      this.basicForm?.get('productType')?.value === 'ASSET_FINANCING_PRODUCT'
    );
  }

  public get isApprovedVendorDisbursement(): boolean {
    return (
      this.isAssetFinancing &&
      this.disbursementForm?.get('disbursementMethod')?.value ===
        'DIRECT_TO_VENDOR_DEALER'
    );
  }

  public get filteredVendorDealers(): VendorDealer[] {
    const query = (this.vendorSearchControl.value || '').trim().toLowerCase();
    if (!query) return this.vendorDealers;
    return this.vendorDealers.filter((vendor) =>
      [
        vendor.name,
        vendor.townCity,
        vendor.state,
        vendor.country,
        vendor.contactPerson,
        vendor.telephone,
      ].some((value) => (value || '').toLowerCase().includes(query)),
    );
  }

  /* The method the product was configured with leads the dropdown and is
     what the loan starts on; the other stays available below it. A product
     that sets none keeps the default order. */
  private applyProductDisbursementMethod(method: string): void {
    const methods = NewLoanAccountComponent.DISBURSEMENT_METHODS;
    const productMethod = methods.find((option) => option.value === method);
    this.disbursementMethods = productMethod
      ? [productMethod, ...methods.filter((option) => option !== productMethod)]
      : [...methods];
    this.disbursementForm
      .get('disbursementMethod')
      .setValue(this.disbursementMethods[0].value);
  }

  /* Tenant credits the borrower's savings account at this institution, never
     a vendor's, so it is not offered while the loan is paid to a vendor. */
  public get availableTransferTypes(): any[] {
    if (!this.isApprovedVendorDisbursement) return this.transferTypeOptions;
    return this.transferTypeOptions.filter(
      (option) => option.value !== 'SAVINGS_ACCOUNT',
    );
  }

  /** the vendor picked in the table, while the loan is paid to a vendor */
  public get selectedVendor(): VendorDealer | null {
    if (!this.isApprovedVendorDisbursement) return null;
    return (
      this.vendorDealers.find((vendor) => vendor.id === this.selectedVendorId) ??
      null
    );
  }

  /** a re-click puts back the vendor's details after a manual edit */
  public selectVendor(vendor: VendorDealer): void {
    this.selectedVendorId = vendor.id;
    this.applyVendorAccount();
  }

  /* Paying a vendor means crediting the vendor's own account, which only a
     Third Party transfer reaches - Tenant credits the borrower's savings
     account here. So the vendor method moves Transfer Type to Third Party as
     soon as it is chosen, and Tenant is not offered while it stays chosen.

     A pick then fills Account to Credit, Bank Name and Account Name from the
     bank details the vendor was saved with. Like the borrower's saved
     account, these are taken as they are: the name enquiry runs only once the
     user edits the account number or bank, or when the vendor has no name
     saved to show. Until a vendor is picked, and for one saved without bank
     details, the fields stay empty rather than hold the borrower's account
     to be paid by mistake. */
  private applyVendorAccount(): void {
    if (!this.isApprovedVendorDisbursement) return;

    const transferType = this.disbursementForm.get('transferType');
    if (transferType.value !== 'THIRD_PARTY') {
      const offered = this.transferTypeOptions?.some(
        (option) => option.value === 'THIRD_PARTY',
      );
      if (!offered) return;
      this.transferTypeBeforeVendor = transferType.value;
      // The transfer-type handler comes back here once Third Party is set.
      transferType.setValue('THIRD_PARTY');
      return;
    }

    const vendor = this.selectedVendor;
    const accountNumber = String(vendor?.accountNumber ?? '').trim();
    const bankId = vendor ? this.vendorBankCode(vendor) : '';
    const accountName = String(vendor?.accountName ?? '').trim();
    this.vendorAccountFilled = true;
    this.isUsingDefaultAccount = false;
    this.errorMsg = '';
    // Not emitted, so the edit watchers that start a name enquiry stay quiet.
    this.disbursementForm.patchValue(
      { accountNumber, bankId, accountName },
      { emitEvent: false },
    );

    if (!vendor) return;
    if (!/^\d{10}$/.test(accountNumber) || !bankId) {
      this.toastr.info(
        $localize`:@@vendor_missing_bank_details:${vendor.name}:name: has no complete bank details saved. Enter the account to credit.`,
      );
    } else if (!accountName) {
      // Account Name cannot be typed into, so a vendor saved without one is looked up.
      this.performNameEnquiry(bankId, accountNumber);
    }
  }

  /* The vendor stores the bank's code, the same key the Bank Name options
     use; one saved with only a bank name is matched by name. */
  private vendorBankCode(vendor: VendorDealer): string {
    const code = String(vendor.bankCode ?? '').trim();
    if (code) return code;
    const name = String(vendor.bankName ?? '').trim().toLowerCase();
    const match = name
      ? this.banks?.find(
          (bank) => String(bank.name).trim().toLowerCase() === name,
        )
      : null;
    return match?.code ?? '';
  }

  public openVendorDealer(vendor?: VendorDealer): void {
    const vendorDialogRef = this.dialog.open(VendorDealerDialogComponent, {
      panelClass: ['modal-product', 'vendor-dealer-dialog-panel'],
      width: '1024px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: false,
      data: {
        vendor: vendor
          ? { ...vendor, assetCategories: [...vendor.assetCategories] }
          : null,
      },
    });

    // The dialog saves through the API, so the picker reloads its list.
    vendorDialogRef.afterClosed().subscribe((saved: boolean) => {
      if (!saved) return;
      this.loadVendorDealers();
    });
  }

  public removeVendorDealer(id: number): void {
    this.vendorDealers = this.vendorDealers.filter(
      (vendor) => vendor.id !== id,
    );
    if (this.selectedVendorId === id) {
      this.selectedVendorId = this.vendorDealers[0]?.id ?? null;
    }
  }

  public onAccountTypeChange(): void {
    this.dialogRef.updateSize('860px');
    this.startOver();
    this.updateLoanProductOptions();

    const assetValue = this.basicForm.get('assetValue');
    const insurancePremium = this.basicForm.get('insurancePremium');

    /* Which fields are open is lockProductDrivenFields' decision, taken by
       permission once a product is chosen; startOver has already locked them
       all. Only what is required, and what is cleared, changes with the type. */
    if (this.isAssetFinancing) {
      assetValue.setValidators(Validators.required);
      insurancePremium.setValidators(Validators.required);

      /* The asset value, premium and down payment all come from the selected
         product (see onLoanProductSelected) rather than placeholder figures.
         Cleared here because switching type also clears the product, and
         figures from the previous one would otherwise sit in the row. */
      this.basicForm.patchValue(
        {
          assetValue: '',
          insurancePremium: '',
          downPaymentPercentage: '',
          totalAssetValue: '',
        },
        { emitEvent: false },
      );
      this.updateAssetFinancingValues();
    } else {
      assetValue.clearValidators();
      insurancePremium.clearValidators();
      assetValue.reset('', { emitEvent: false });
      insurancePremium.reset('', { emitEvent: false });
      this.basicForm.patchValue(
        {
          totalAssetValue: '',
          downPaymentPercentage: '',
          principal: '',
        },
        { emitEvent: false },
      );
    }

    assetValue.updateValueAndValidity({ emitEvent: false });
    insurancePremium.updateValueAndValidity({ emitEvent: false });
  }

  /* Only products of the chosen Type. The list API carries the type as
     productCategory ("STANDARD" / "ASSET_FINANCING"), and older products carry
     none, so anything not asset financing is a standard loan - the same rule
     the loan product form uses when it opens a product. */
  private updateLoanProductOptions(): void {
    const selectedType = this.basicForm?.get('productType')?.value;
    const records = this.loanProductRecordsList.filter((record) => {
      const type =
        record?.productCategory === 'ASSET_FINANCING'
          ? 'ASSET_FINANCING_PRODUCT'
          : 'LOAN_PRODUCT';
      return type === selectedType;
    });
    this.loanProducts = this.utils.mapRecordsToSearchableSelect(records);
  }

  private updateAssetFinancingValues(): void {
    if (!this.isAssetFinancing) return;

    const assetValue = this.toNumber(this.basicForm.get('assetValue')?.value);
    const insurancePremium = this.toNumber(
      this.basicForm.get('insurancePremium')?.value,
    );
    const downPaymentPercentage = this.toNumber(
      this.basicForm.get('downPaymentPercentage')?.value,
    );
    // A percentage premium is a share of the asset value, not an amount to add
    // outright — adding 1 to the total for "1%" would be plainly wrong.
    const premiumAmount =
      this.basicForm.get('insurancePremiumType')?.value ===
      'PERCENTAGE_OF_ASSET_VALUE'
        ? assetValue * (insurancePremium / 100)
        : insurancePremium;
    const totalAssetValue = assetValue + premiumAmount;
    // Down payment is a percentage of the complete financed asset value.
    const downPayment = totalAssetValue * (downPaymentPercentage / 100);

    this.basicForm.patchValue(
      {
        totalAssetValue,
        principal: Math.max(0, totalAssetValue - downPayment),
      },
      { emitEvent: false },
    );
  }

  /* The loan is in its product's currency - the product fetch returns the
     code, e.g. "USD". Until a product is chosen, or if one sets none, it is
     the tenant's own, from the currency service. */
  public get loanCurrency(): string {
    const productCurrency = this.loanProductRecords?.currency;
    return (
      (typeof productCurrency === 'string' && productCurrency) ||
      this.currencyFormatter.getCurrencyCode()
    );
  }

  public get titleHoldingModelLabel(): string {
    return this.readableValue(
      this.loanProductRecords?.titleHoldingModel ||
        this.loanProductRecords?.titleHoldingModelType,
    );
  }

  public readableValue(value: any): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value)
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /* `plural` renders the unit as "week(s)" rather than committing to one form.
     These labels sit beside a number the user is still editing, so the field
     would otherwise read "1 weeks" one keystroke and "2 week" the next. */
  public periodUnitLabel(value: string, plural = false): string {
    const labels: Record<string, string> = {
      DAY: 'day',
      WEEK: 'week',
      MONTH: 'month',
      YEAR: 'year',
      TENOR: 'tenor',
    };
    const label = labels[value] || String(value || '').toLowerCase();
    return plural && label ? `${label}(s)` : label;
  }

  /* The Interest Rate input is only as wide as its figure, so the % after it
     sits right beside the number rather than at the far end of the box. */
  public get interestRateWidth(): number {
    const text = String(this.basicForm?.get('interestRate')?.value ?? '');
    // Half a character spare, so the caret at the end does not scroll it.
    return Math.max(text.length, 1) + 0.5;
  }

  public get hasInterestRate(): boolean {
    return this.hasValue('interestRate');
  }

  /* The penalty amount on a percentage basis gets the same treatment, so its
     % sits beside the figure instead of at the far end of the box. On Flat
     the symbol is part of the text and the input keeps its full width. */
  public get premiumWidth(): number | null {
    if (this.premiumIsFlat) return null;
    const text = String(this.basicForm?.get('insurancePremium')?.value ?? '');
    return Math.max(text.length, 1) + 0.5;
  }

  public get downPaymentWidth(): number {
    const text = String(this.basicForm?.get('downPaymentPercentage')?.value ?? '');
    return Math.max(text.length, 1) + 0.5;
  }

  public get penaltyAmountWidth(): number | null {
    if (!this.penaltyBasis || this.penaltyBasis === 'FLAT') return null;
    return Math.max(this.penaltyAmountText.length, 1) + 0.5;
  }

  /* A % or currency symbol follows a figure only once there is one - an
     empty box shows nothing in it. 0 counts as a figure. */
  public hasValue(control: string): boolean {
    const value = this.basicForm?.get(control)?.value;
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  public get hasPenaltyAmount(): boolean {
    const value = this.penaltyFieldValue(
      'missedPaymentPenaltyAmount',
      this.productLatePayment.amount,
    );
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  public get amortizationLabel(): string {
    const value = this.basicForm?.get('amortizationType')?.value;
    return this.readableValue(value === 'NORMAL' ? 'FLAT' : value);
  }

  public get amortizationDescription(): string {
    const descriptions: Record<string, string> = {
      NORMAL:
        'Interest is calculated on the principal for the full loan term. The interest portion of each installment stays constant even as the loan is paid down - the effective/true interest rate is higher than the quoted flat rate.',
      FLAT: 'Interest is calculated on the principal for the full loan term. The interest portion of each installment stays constant even as the loan is paid down - the effective/true interest rate is higher than the quoted flat rate.',
      REDUCING_BALANCE:
        'Interest is calculated on the outstanding loan balance, so the interest portion falls as the principal is repaid.',
      STRAIGHT_LINE:
        'Principal is divided evenly across the loan term while interest is calculated on the outstanding balance.',
      BALLOON:
        'Interest is paid during the loan term and the principal becomes due in the final installment.',
      UPFRONT_INTEREST:
        'The total interest for the loan term is collected with the first installment.',
    };
    const value = this.basicForm?.get('amortizationType')?.value;
    return descriptions[value] || descriptions['NORMAL'];
  }

  /* The product sends the basis as a flag, isCalenderDays (the API's own
     spelling; the correct one is accepted too). Passing the flag itself to
     readableValue printed "True", so it is turned into the basis it stands
     for. An explicit calculationBasis, when sent, still wins. */
  public get calculationBasisLabel(): string {
    const product = this.loanProductRecords || {};
    if (product.calculationBasis) {
      return this.readableValue(product.calculationBasis);
    }
    const calendarDays = product.isCalenderDays ?? product.isCalendarDays;
    return this.readableValue(calendarDays ? 'CALENDAR_DAYS' : 'WORKING_DAYS');
  }

  public get requiresCompulsorySavings(): boolean {
    const product = this.loanProductRecords;
    if (typeof product?.requireCompulsorySavings === 'boolean') {
      return product.requireCompulsorySavings;
    }
    const type = product?.compulsorySavingsType;
    return !!type && type !== 'NONE';
  }

  public get compulsorySavingsEnabled(): boolean {
    return this.isPanelSwitchOn(
      'compulsorySavings',
      this.requiresCompulsorySavings,
    );
  }

  public get compulsorySavingsAccount(): string {
    return (
      this.loanProductRecords?.savingsProduct?.name ||
      this.loanProductRecords?.savingsProductName ||
      ''
    );
  }

  public get missedSavingsPenalty(): string {
    const product = this.loanProductRecords || {};
    return this.readableValue(
      product.missedSavingsPenalty ||
        product.missedContributionPenalty ||
        product.missedContributionPenaltyType,
    );
  }

  /* `key` is what the panel overrides and the create payload are keyed on.
     The label cannot serve that purpose: it is translated, so a non-English
     locale would key the same row differently and lose the user's override. */
  public get guaranteeDetails(): Array<{
    key: 'guarantor' | 'physicalAsset' | 'fixedDepositLien';
    label: string;
    enabled: boolean;
    coverage: string;
  }> {
    const product = this.loanProductRecords || {};
    const percentage = (value: any) =>
      value === null || value === undefined || value === '' ? '-' : `${value}%`;

    if (this.isAssetFinancing) {
      return [
        {
          key: 'guarantor',
          label: $localize`:@@require_guarantor:Require Guarantor`,
          enabled: !!product.requireGuarantor,
          coverage: percentage(product.guarantorMinimumCoveragePercent),
        },
        {
          // Asset financing calls it "Other Asset(s)"; the API field is the
          // same requirePhysicalAsset either way.
          key: 'physicalAsset',
          label: $localize`:@@require_other_assets:Require Other Asset(s)`,
          enabled: !!product.requirePhysicalAsset,
          coverage: percentage(product.physicalAssetMinimumCoveragePercent),
        },
      ];
    }

    return [
      {
        key: 'guarantor',
        label: $localize`:@@require_guarantor:Require Guarantor`,
        enabled: !!product.requireGuarantor,
        coverage: percentage(product.guarantorMinimumCoveragePercent),
      },
      {
        key: 'physicalAsset',
        label: $localize`:@@physical_assets:Physical Asset(s)`,
        enabled: !!product.requirePhysicalAsset,
        coverage: percentage(product.physicalAssetMinimumCoveragePercent),
      },
      {
        key: 'fixedDepositLien',
        label: $localize`:@@fixed_deposit_lien:Fixed Deposit Lien`,
        enabled: !!product.requireFixedDepositLien,
        coverage: percentage(product.fixedDepositLienMinimumCoveragePercent),
      },
    ];
  }

  public get productFees(): ProductFeeView[] {
    const product = this.loanProductRecords || {};
    const fees =
      product.feeCharges ||
      product.feesAndCharges ||
      product.chargeConfigurations ||
      product.charges ||
      [];
    if (!Array.isArray(fees)) return [];
    return fees.map((fee: any) => {
      /* The product API returns feeEvent / chargeAmount / minValue / maxValue.
         The older spellings are kept as fallbacks so a product saved before the
         rename still renders. */
      const rawEvent =
        fee.feeEvent || fee.eventType || fee.event || fee.frequency || 'DAILY';
      const eventType = String(rawEvent).toUpperCase().includes('QUARTER')
        ? 'QUARTERLY'
        : 'DAILY';
      return {
        name: fee.name || fee.chargeName || fee.charge?.name || '-',
        value: this.toNumber(fee.chargeAmount ?? fee.value ?? fee.amount),
        minimum: this.toNumber(
          fee.minValue ?? fee.minimumValue ?? fee.minimumAmount,
        ),
        maximum: this.toNumber(
          fee.maxValue ?? fee.maximumValue ?? fee.maximumAmount,
        ),
        event: this.readableValue(rawEvent),
        eventType,
        // Only a TIME-scheduled event carries a time; the rest set scheduleDetail
        // to a weekday, a day of the month or a month name.
        eventTime: this.scheduleDetailLabel(
          fee.scheduleDetail || fee.eventTime || fee.time,
        ),
      };
    });
  }

  /* A fee's schedule detail as the API spells it (FIRST_DAY, MONDAY, JANUARY,
     15, 23:59) in words, as the loan product form shows it: First Day,
     Monday, January, Day 15, 11:59PM. */
  private scheduleDetailLabel(detail: any): string {
    const text = String(detail ?? '').trim();
    if (!text) return '';

    // Already 12-hour, as older products and the preview rows carry it.
    if (/^\d{1,2}:\d{2}\s?[AP]M$/i.test(text)) return text.toUpperCase();

    const time = /^(\d{1,2}):(\d{2})$/.exec(text);
    if (time) {
      const hour = Number(time[1]);
      return `${hour % 12 || 12}:${time[2]}${hour < 12 ? 'AM' : 'PM'}`;
    }

    // A bare number is a custom day of the month.
    if (/^\d+$/.test(text)) return $localize`:@@day_of_month:Day ${text}:day:`;

    return this.readableValue(text);
  }

  public get displayedProductFees(): ProductFeeView[] {
    const configuredFees = this.productFees;
    if (configuredFees.length) {
      return configuredFees;
    }

    return [
      {
        name: 'Processing Fee',
        value: 5,
        minimum: 5,
        maximum: 5,
        event: 'Daily',
        eventType: 'DAILY',
        eventTime: '11:59PM',
      },
      {
        name: 'Admin. Fee',
        value: 5,
        minimum: 5,
        maximum: 5,
        event: 'Quarterly',
        eventType: 'QUARTERLY',
      },
      {
        name: 'Search Fee',
        value: 5,
        minimum: 5,
        maximum: 5,
        event: 'Daily',
        eventType: 'DAILY',
        eventTime: '11:59PM',
      },
    ];
  }

  public formatProductAmount(value: number): string {
    if (!value) return '-';
    return `${this.getCurrencySymbol()}${value.toLocaleString(undefined, {
      maximumFractionDigits: 2,
    })}`;
  }

  private toNumber(value: any): number {
    const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /* The schedule comes from the backend, so the figures here are the ones the
     loan will actually post. It is fetched once when a product is chosen and
     after that only on Calculate, as on the loan product calculator: the
     button appears once an edit leaves the schedule on screen behind the
     form, rather than the endpoint being called on every keystroke. */
  public get scheduleOutOfDate(): boolean {
    return (
      !!this.loanProductRecords &&
      JSON.stringify(this.schedulePayload()) !== this.calculatedFrom
    );
  }

  /* The Calculate button passes showProgress, so it stays up reading
     "Calculating" until the schedule arrives; the fetch on choosing a
     product shows no button at all. */
  public calculateSchedule(showProgress = false): void {
    const payload = this.schedulePayload();
    // Only the latest request may fill the table.
    this.scheduleRequest?.unsubscribe();
    this.calculatedFrom = JSON.stringify(payload);

    if (!payload.principal || !payload.loanPeriodLength) {
      this.clearSchedule();
      if (showProgress) {
        this.notification.error(
          $localize`:@@enter_loan_size_and_term:Enter a loan size and loan term to calculate`,
        );
      }
      return;
    }

    this.calculating = showProgress;
    this.scheduleRequest = this.loanProductService
      .getRepaymentSchedule(payload)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => {
          this.calculating = false;
          this.applySchedule(res?.data ?? res);
        },
        error: (err) => {
          this.clearSchedule();
          // Still out of date, so Calculate stays up to try again. Said out
          // loud because the interceptor is silent on 504 and status 0.
          this.calculatedFrom = null;
          this.notification.error(
            err?.error?.message ||
              'Could not calculate the repayment schedule. Please try again.',
          );
        },
      });
  }

  /** what the calculator is asked for, from the form as it stands */
  private schedulePayload(): any {
    const values = this.basicForm.getRawValue();
    const payload: any = {
      loanProductId: values.loanProductId,
      principal: this.toNumber(values.principal),
      interestRate: this.toNumber(values.interestRate),
      interestRateUnit: values.interestRateUnit,
      loanPeriodLength: this.toNumber(values.loanPeriodLength),
      loanPeriodLengthUnit: values.loanPeriodLengthUnit,
      loanRepaymentFrequency: this.toNumber(values.loanRepaymentFrequency),
      loanRepaymentFrequencyPeriodUnit: values.loanRepaymentFrequencyPeriodUnit,
      amortizationType: values.amortizationType,
      ...this.compulsorySavingsPanelPayload(),
    };

    if (values.amortizationType === 'BALLOON') {
      payload.balloonPaymentPercent = this.toNumber(
        this.loanProductRecords?.defaultBalloonPaymentPercent,
      );
    }

    if (this.isAssetFinancing) {
      const assetValue = this.toNumber(values.assetValue);
      const premium = this.toNumber(values.insurancePremium);
      payload.productCategory = 'ASSET_FINANCING';
      payload.assetValue = assetValue;
      payload.enableInsurancePremium =
        !!this.loanProductRecords?.enableInsurancePremium;
      /* The calculator takes the premium as a percentage of the asset value,
         so a flat premium goes as the share of the asset it comes to. */
      payload.insuranceAnnualPremiumPercent =
        values.insurancePremiumType === 'FLAT' && assetValue
          ? (premium / assetValue) * 100
          : premium;
    }

    return payload;
  }

  private clearSchedule(): void {
    this.scheduleRequest?.unsubscribe();
    this.calculating = false;
    this.repaymentSchedule = [];
    this.scheduleTotals = {
      principal: 0,
      interest: 0,
      fees: 0,
      savings: 0,
      total: 0,
    };
  }

  /* Maps the API's schedule onto this table's columns. Row 0 is the
     disbursement, carrying any upfront charge, so it is kept - the totals
     include it. */
  private applySchedule(data: any): void {
    const rows = data?.schedule ?? [];

    this.repaymentSchedule = rows.map((row: any, index: number) => ({
      period: row.repaymentNo ?? index,
      opening: this.toNumber(row.beginningBalance),
      principal: this.toNumber(row.principal),
      interest: this.toNumber(row.interest),
      closing: this.toNumber(row.endingBalance),
      fees: this.toNumber(row.charge),
      savings: this.toNumber(row.savings),
      total: this.toNumber(row.amountDueTotal),
    }));

    const sum = (pick: (row: LoanScheduleRow) => number) =>
      this.repaymentSchedule.reduce((total, row) => total + pick(row), 0);

    // The totals block names two of these differently from the rows.
    const totals = data?.totals ?? {};
    this.scheduleTotals = {
      principal: this.toNumber(totals.principal) || sum((row) => row.principal),
      interest: this.toNumber(totals.interest) || sum((row) => row.interest),
      fees: this.toNumber(totals.charge) || sum((row) => row.fees),
      savings: this.toNumber(totals.savings) || sum((row) => row.savings),
      total: this.toNumber(totals.instalmentTotal) || sum((row) => row.total),
    };
  }
  private daysFor(unit: string): number {
    const days: Record<string, number> = {
      DAY: 1,
      WEEK: 7,
      MONTH: 30,
      YEAR: 365,
    };
    return days[unit] || 1;
  }

  private compulsorySavingsPerPeriod(
    principal: number,
    instalment: number,
  ): number {
    if (!this.requiresCompulsorySavings) return 0;
    const type = this.loanProductRecords?.compulsorySavingsType;
    const amount = this.toNumber(
      this.loanProductRecords?.compulsorySavingsAmount,
    );
    if (type === 'PERCENTAGE_OF_PRINCIPAL') {
      return (principal * amount) / 100;
    }
    if (type === 'PERCENTAGE_OF_INSTALMENT') {
      return (instalment * amount) / 100;
    }
    return amount;
  }

  navigateToState(val: string) {
    // Validate basic form before moving to other tabs
    if (val === 'disbursementDetails' || val === 'guarantor') {
      // Loan Term is editable now, so a tenor error from an earlier attempt
      // would otherwise block this route too, silently.
      this.clearStaleTenorError();
      if (this.basicForm.invalid) {
        this.notifyBasicErrors();
        this.stayOn('basic');
        return;
      }

      if (!this.termsWithinRange()) {
        this.stayOn('basic');
        return;
      }
    }

    if (
      val === 'disbursementDetails' &&
      this.loanProductRecords?.requireGuarantor
    ) {
      if (this.guarantorForm.invalid) {
        this.validatorService.CheckMessagesAndNotify(
          this.guarantorForm.controls,
        );
        this.stayOn('guarantor');
        return;
      }
    }

    this.toggleStates.forEach((state) => {
      state.isActive = state.value === val;
    });
    this.activeTab = val;
    if (val === 'disbursementDetails') {
      this.dialogRef.updateSize('860px');
    }
  }

  onStateSelected() {
    this.guarantorForm.controls['stateId'].valueChanges
      .pipe(
        filter((stateKey) => !!stateKey),
        takeUntil(this.unsubscribe$),
        switchMap((stateKey) =>
          this.countryService
            .getLocalGovernmentsByStateCode(this.toStateCode(stateKey, this.rawStates))
            .pipe(
            catchError(() => of(null)),
          ),
        ),
      )
      .subscribe((res) => {
        if (!res) return;
        const lgaList = res?.data?.localGovernments ?? [];
        this.rawLocalGovernments = lgaList;
        this.localGovernments = lgaList.map((lga: any) => ({
          label: lga.name,
          value: lga.uuid,
        }));
        this.guarantorForm.get('lgaId')?.setValue('', { emitEvent: false });
      });

    this.guarantorForm.controls['businessStateId'].valueChanges
      .pipe(
        filter((stateKey) => !!stateKey),
        takeUntil(this.unsubscribe$),
        switchMap((stateKey) =>
          this.countryService
            .getLocalGovernmentsByStateCode(
              this.toStateCode(stateKey, this.rawBusinessStates),
            )
            .pipe(
            catchError(() => of(null)),
          ),
        ),
      )
      .subscribe((res) => {
        if (!res) return;
        const lgaList = res?.data?.localGovernments ?? [];
        this.rawBusinessLocalGovernments = lgaList;
        this.businessLocalGovernments = lgaList.map((lga: any) => ({
          label: lga.name,
          value: lga.uuid,
        }));
        this.guarantorForm
          .get('businessLgaId')
          ?.setValue('', { emitEvent: false });
      });
  }

  private resetGuarantorState(): void {
    this.foundGuarantorId = null;
    this.hasVerifiedIdentity = false;
    this.foundGuarantorIdentifierVerified = false;
    this.identifierRequired = false;
    this.isViewMode = false;
    this.isBioDataEditing = false;
    this.guarantorPhoneSearchIdentifierType = null;
    this.localGovernments = [];
    this.businessLocalGovernments = [];
    this.imageValues = { photo: '', signature: '', idCard: '' };
    // Preserve the current identifier type so the dropdown doesn't reset mid-flow
    const currentIdentifierType =
      this.guarantorForm.get('identifierType')?.value;
    this.guarantorForm.reset({}, { emitEvent: false });
    this.guarantorForm
      .get('identifierType')
      ?.setValue(currentIdentifierType, { emitEvent: false });
    this.guarantorForm.enable({ emitEvent: false });
    if (this.identityVerificationEnabled) {
      const bioFields = [
        'firstName',
        'lastName',
        'middleName',
        'gender',
        'dateOfBirth',
        'email',
        'mobileNumber',
      ];
      bioFields.forEach((field) =>
        this.guarantorForm.get(field)?.disable({ emitEvent: false }),
      );
    }
    // Restore default country so states reload and phone flag resets
    if (this.tenantCountryCode) {
      this.guarantorForm.get('countryId')?.setValue(this.tenantCountryCode);
      this.guarantorForm
        .get('businessCountryId')
        ?.setValue(this.tenantCountryCode);
    }
    this.searchInput.setValue('', { emitEvent: false });
  }

  verifyGuarantorIdentity() {
    const searchValue = this.searchInput.value?.trim();
    const identifierType = this.identityVerificationEnabled
      ? this.guarantorForm.get('identifierType')?.value || 'BVN'
      : 'Phone';
    if (!searchValue || this.verifyingIdentity) return;
    this.verifyingIdentity = true;

    if (identifierType === 'Phone') {
      // Search by phone number
      this.guarantorService
        .searchGuarantor({ mobileNumber: searchValue })
        .subscribe({
          next: (result) => {
            this.verifyingIdentity = false;
            const guarantor = result?.data?.guarantor;
            if (!result.successful || !result.data?.found || !guarantor) {
              this.resetGuarantorState();
              this.notification.info(
                $localize`:@@no_guarantor_found_with_phone:No guarantor found with this phone number.`,
              );
              return;
            }
            if (
              result.data?.guaranteeingActiveLoan &&
              guarantor?.guaranteeMultipleLoans === false
            ) {
              this.resetGuarantorState();
              this.notification.error(
                $localize`:@@guarantor_is_guaranteeing_an_active_loan_and_cannot_be_used:Guarantor is guaranteeing an active loan and cannot be used`,
              );
              return;
            }
            this.foundGuarantorId = guarantor.id;
            this.identifierRequired = result.data?.identifierRequired ?? false;
            this.foundGuarantorIdentifierVerified =
              guarantor.identifierVerified ?? false;
            // Enable entire form first so all setValue calls update the view correctly
            this.guarantorForm.enable({ emitEvent: false });
            this.setGuaratorRecords(guarantor);
            this.loadPrefilledGuarantorLgas();
            // Store the guarantor's actual identifier type for the submit payload.
            // We do NOT change the search dropdown (identifierType form control) so it
            // stays on "Phone" and never shows an empty option.
            this.guarantorPhoneSearchIdentifierType =
              guarantor.identifierType || this.primaryNationalIdentifier;
            this.guarantorForm.disable({ emitEvent: false });
            this.isBioDataEditing = false;
            this.guarantorForm
              .get('mobileNumber')
              ?.enable({ emitEvent: false });
            if (guarantor.mobileNumber) {
              const formattedPhone = this.utils.formatPhoneForCountry(
                guarantor.mobileNumber,
                this.currencyFormatter.tenantCountryISO,
              );
              this.guarantorForm
                .get('mobileNumber')
                ?.setValue(formattedPhone, { emitEvent: false });
            }
            if (!this.foundGuarantorIdentifierVerified) {
              // Identifier not yet verified — let user pick the type and enter it
              this.guarantorForm
                .get('identifierType')
                ?.enable({ emitEvent: false });
              this.guarantorForm.get('bvn')?.enable({ emitEvent: false });
            }
            this.isViewMode = true;
            this.notification.success(
              $localize`:@@guarantor_found_and_available_to_guarantee_a_loan:Guarantor found and available to guarantee a loan`,
            );
          },
          // The interceptor already toasts the backend's own message for every
          // status it doesn't stay silent on (e.g. "Guarantor is not allowed to
          // guarantee multiple loans") — showing a generic one here duplicated it.
          error: (err) => {
            this.verifyingIdentity = false;
            this.resetGuarantorState();
            if (err?.status === 504 || err?.status === 0) {
              this.notification.error(
                $localize`:@@search_failed:Search failed. Please try again.`,
              );
            }
          },
        });
    } else {
      // BVN/NIN — set the form identifier field then verify externally
      this.guarantorForm
        .get('bvn')
        ?.setValue(searchValue, { emitEvent: false });
      this.verifyIdentityExternally(searchValue, identifierType);
    }
  }

  /**
   * Fuzzy-matches a text string against a loaded list of SearchableSelectTypes.
   * Tries exact match first, then case-insensitive contains in both directions
   * (handles "Lagos State" ↔ "Lagos" mismatches between the verify portal and country service).
   */
  private fuzzyMatchOption(
    list: SearchableSelectTypes[],
    searchTerm: string,
  ): SearchableSelectTypes | null {
    if (!searchTerm || !list.length) return null;
    const term = searchTerm.toLowerCase().trim();
    const exact = list.find((item) => item.label.toLowerCase() === term);
    if (exact) return exact;
    return (
      list.find(
        (item) =>
          item.label.toLowerCase().includes(term) ||
          term.includes(item.label.toLowerCase()),
      ) || null
    );
  }

  private verifyIdentityExternally(
    identifier: string,
    identifierType: string,
  ): void {
    this.clientService
      .verifyClient({
        countryCode: this.organization?.tenantCountry?.countryCode,
        identifierType,
        identifier,
      })
      .subscribe({
        next: (res) => {
          this.verifyingIdentity = false;
          if (!res.successful) {
            this.notification.error(
              identityVerificationMessage(res, identifierType),
            );
            return;
          }
          const data = res.data?.data;
          let gender = data?.gender?.toUpperCase();
          if (gender === 'F') gender = 'FEMALE';
          else if (gender === 'M') gender = 'MALE';
          this.guarantorForm.patchValue({
            firstName: data?.firstName || '',
            lastName: data?.lastName || '',
            middleName: data?.middleName || '',
            gender: gender || '',
            email: data?.email || '',
          });
          if (data?.dateOfBirth) {
            const dob = new Date(data.dateOfBirth);
            if (!isNaN(dob.getTime())) {
              this.guarantorForm.get('dateOfBirth')?.setValue(dob);
            }
          }
          if (data?.phoneNumber1) {
            const formattedPhone = this.utils.formatPhoneForCountry(
              data.phoneNumber1,
              this.currencyFormatter.tenantCountryISO,
            );
            this.guarantorForm
              .get('mobileNumber')
              ?.setValue(formattedPhone, { emitEvent: false });
          }
          if (data?.base64Image) {
            const photoData = `data:image/jpeg;base64,${data.base64Image}`;
            this.imageValues.photo = photoData;
            this.guarantorForm.get('photo')?.setValue(photoData);
          }

          // Try to match state/LGA strings from the verify response against the loaded lists
          const stateString =
            data?.state || data?.stateName || data?.stateOfOrigin || '';
          const lgaString =
            data?.lga || data?.lgaName || data?.localGovernment || '';
          if (stateString) {
            const matchedState = this.fuzzyMatchOption(
              this.states,
              stateString,
            );
            if (matchedState) {
              this.guarantorForm
                .get('stateId')
                ?.setValue(matchedState.value, { emitEvent: false });
              // Load LGAs for the matched state, then try to match the LGA string
              this.countryService
                .getLocalGovernmentsByStateCode(
                  this.toStateCode(matchedState.value, this.rawStates),
                )
                .subscribe((lgaRes) => {
                  const lgaList = lgaRes?.data?.localGovernments ?? [];
                  this.rawLocalGovernments = lgaList;
                  this.localGovernments = lgaList.map((lga: any) => ({
                    label: lga.name,
                    value: lga.uuid,
                  }));
                  if (lgaString) {
                    const matchedLga = this.fuzzyMatchOption(
                      this.localGovernments,
                      lgaString,
                    );
                    if (matchedLga) {
                      this.guarantorForm
                        .get('lgaId')
                        ?.setValue(matchedLga.value, { emitEvent: false });
                    }
                  }
                });
            }
          }

          const fieldsToDisable: string[] = [];
          if (data?.firstName) fieldsToDisable.push('firstName');
          if (data?.lastName) fieldsToDisable.push('lastName');
          if (data?.middleName) fieldsToDisable.push('middleName');
          if (gender) fieldsToDisable.push('gender');
          if (data?.dateOfBirth) fieldsToDisable.push('dateOfBirth');
          if (data?.email) fieldsToDisable.push('email');
          if (data?.phoneNumber1) fieldsToDisable.push('mobileNumber');
          fieldsToDisable.forEach((field) =>
            this.guarantorForm.get(field)?.disable({ emitEvent: false }),
          );
          this.hasVerifiedIdentity = true;
          this.foundGuarantorIdentifierVerified = true;
          this.notification.success(
            $localize`:@@identity_verified:Identity verified successfully`,
          );
        },
        error: (err) => {
          this.verifyingIdentity = false;
          this.notification.error(
            identityVerificationMessage(err, identifierType),
          );
        },
      });
  }

  get canEditBioData(): boolean {
    return this.permissionService.hasPermissionList([
      Permissions.EDIT_BVN_DETAILS,
    ]);
  }

  get primaryNationalIdentifier(): string {
    return this.nationalIdentifiers[0] || 'BVN';
  }

  /* Bio Data's identifier field is labelled with whichever financial
     identifier is actually in play: the one chosen in the search dropdown,
     or — when the guarantor was found by phone, where the dropdown stays on
     "Phone" so it never shows an empty option — the identifier type that
     guarantor carries. Falls back to the tenant's primary identifier. */
  get guarantorIdentifierLabel(): string {
    const selected = this.guarantorForm?.get('identifierType')?.value;
    if (selected && selected !== 'Phone') return selected;
    return (
      this.guarantorPhoneSearchIdentifierType || this.primaryNationalIdentifier
    );
  }

  /* The identifier is typed by hand only where no verification fills it: a
     tenant with verification off, or a guarantor found by phone whose
     identifier is still unverified — and only for a user holding
     EDIT_BVN_DETAILS. Otherwise it reads back the search. */
  get bvnLocked(): boolean {
    if (!this.canEditBioData) return true;
    if (this.foundGuarantorIdentifierVerified || this.hasVerifiedIdentity)
      return true;
    return this.identityVerificationEnabled && !this.foundGuarantorId;
  }

  enableBioDataFields() {
    this.isBioDataEditing = true;
    this.isViewMode = false;
    this.guarantorForm.enable({ emitEvent: false });
  }

  disableBioDataFields() {
    this.isBioDataEditing = false;
    this.isViewMode = !!this.foundGuarantorId;
    this.guarantorForm.disable({ emitEvent: false });
    // Keep mobileNumber editable so the user can always correct it
    this.guarantorForm.get('mobileNumber')?.enable({ emitEvent: false });
    // Keep identifier fields editable if BVN/NIN hasn't been verified yet
    if (!this.foundGuarantorIdentifierVerified && !this.hasVerifiedIdentity) {
      this.guarantorForm.get('identifierType')?.enable({ emitEvent: false });
      this.guarantorForm.get('bvn')?.enable({ emitEvent: false });
    }
  }

  setGuaratorRecords(records: any, clear?: boolean) {
    records = {
      ...records,
      bvn: records?.identifier ?? records?.bvn,
      // The dropdowns bind country-data uuids, which the API returns as the flat
      // stateId/lgaId; the state/lga objects only exist for legacy numeric rows.
      stateId: records?.stateId ?? records?.state?.uuid ?? records?.state?.id,
      lgaId: records?.lgaId ?? records?.lga?.uuid ?? records?.lga?.id,
    };
    const keys = Object.keys(records);
    const excludedKeys = ['mobileNumber'];
    keys.forEach((key) => {
      if (!this.guarantorForm.contains(key) || excludedKeys.includes(key))
        return;
      const value = clear ? '' : records[key];
      if (!clear && (value === undefined || value === null)) return;
      this.guarantorForm.controls[key].setValue(value, { emitEvent: false });
    });
    /* The record calls it passportPhoto while the control is named photo, so
       the loop above skips it and the required validator keeps failing on an
       existing guarantor whose photo is already on file. Signature and idCard
       match by name and are set by the loop. The control holds the stored
       value, not the display URL, so submitting sends back what the API gave
       us rather than a resolved path. */
    this.guarantorForm.controls['photo'].setValue(
      clear ? '' : (records?.passportPhoto ?? ''),
      { emitEvent: false },
    );

    const imgPath = environment.baseUrl + environment.coreUrl + 'data/';
    this.imageValues.photo = clear
      ? null
      : `${imgPath}${records?.passportPhoto}`;
    this.imageValues.signature = clear
      ? null
      : `${imgPath}${records?.signature}`;
    this.imageValues.idCard = clear ? null : `${imgPath}${records?.idCard}`;
  }

  /* ------------------------------------------------ live range checks */

  /* The adjustable terms are checked against the product's range as they
     are typed, so a breach shows on the field the moment it happens rather
     than only at Next. Each is null where no range applies: no product yet,
     a unit changed away from the product's (its range then says nothing
     about the figure - see unitChanged), or a field the type does not use. */
  private termRange(control: string): {
    label: string;
    min: any;
    max: any;
    format: (figure: number) => string;
  } | null {
    const product = this.loanProductRecords;
    if (!product || !this.basicForm) return null;

    const periods = (unitControl: string) => (figure: number) => {
      const unit = this.periodUnitLabel(this.basicForm.get(unitControl)?.value);
      return `${figure} ${unit}${figure === 1 ? '' : 's'}`;
    };
    const percent = (figure: number) => `${figure}%`;

    switch (control) {
      case 'principal':
        // As validateLoanSize: asset financing's derived loan size is not
        // held to the range yet (its TEMPORARY override).
        if (this.isAssetFinancing) return null;
        return {
          label: $localize`:@@loanSizeLabel:Loan Size`,
          min: product.minimumPrincipal,
          max: product.maximumPrincipal,
          format: (figure) => this.moneyText(figure),
        };
      case 'interestRate':
        if (this.unitChanged('interestRateUnit')) return null;
        return {
          label: $localize`:@@interestRateLabel:Interest Rate`,
          min: product.minimumInterestRate,
          max: product.maximumInterestRate,
          format: percent,
        };
      case 'loanPeriodLength':
        if (this.unitChanged('loanPeriodLengthUnit')) return null;
        return {
          label: $localize`:@@loanTermLabel:Loan Term`,
          min: product.minimumLoanPeriodLength,
          max: product.maximumLoanPeriodLength,
          format: periods('loanPeriodLengthUnit'),
        };
      case 'loanRepaymentFrequency':
        if (this.unitChanged('loanRepaymentFrequencyPeriodUnit')) return null;
        return {
          label: $localize`:@@repaymentFrequencyLabel:Repayment Frequency`,
          min: product.minimumLoanRepaymentFrequency,
          max: product.maximumLoanRepaymentFrequency,
          format: periods('loanRepaymentFrequencyPeriodUnit'),
        };
      case 'gracePeriodLength':
        if (this.unitChanged('gracePeriodUnit')) return null;
        return {
          label: $localize`:@@gracePeriodLabel:Grace Period`,
          min: product.minimumGracePeriod,
          max: product.maximumGracePeriod,
          format: periods('gracePeriodUnit'),
        };
      // A share of the asset: the product's floor, and never more than all of it.
      case 'downPaymentPercentage':
        if (!this.isAssetFinancing) return null;
        return {
          label: $localize`:@@down_payment:Down Payment`,
          min:
            product.minimumDownPaymentPercent ??
            product.minimumDownPayment ??
            product.minimumDownPaymentPercentage,
          max: 100,
          format: percent,
        };
      // A percentage of the asset value runs 0 to 100; a flat premium has no cap here.
      case 'insurancePremium':
        if (!this.isAssetFinancing || this.premiumIsFlat) return null;
        return {
          label: $localize`:@@insurance_premium:Insurance Premium`,
          min: 0,
          max: 100,
          format: percent,
        };
    }
    return null;
  }

  /** a figure outside its range, in full for the toast and in short for the field */
  private rangeBreach(
    value: any,
    range: {
      label: string;
      min: any;
      max: any;
      format: (figure: number) => string;
    } | null,
  ): { message: string; short: string } | null {
    if (!range || value === null || value === undefined) return null;
    if (String(value).trim() === '') return null;

    const figure = this.toNumber(value);
    const isSet = (bound: any) =>
      bound !== null && bound !== undefined && bound !== '';

    if (isSet(range.max) && figure > Number(range.max)) {
      const bound = range.format(Number(range.max));
      return {
        message: $localize`:@@term_above_maximum:${range.label}:label: cannot be more than the maximum of ${bound}:bound:`,
        short: $localize`:@@term_maximum_short:Maximum is ${bound}:bound:`,
      };
    }
    if (isSet(range.min) && figure < Number(range.min)) {
      const bound = range.format(Number(range.min));
      return {
        message: $localize`:@@term_below_minimum:${range.label}:label: cannot be less than the minimum of ${bound}:bound:`,
        short: $localize`:@@term_minimum_short:Minimum is ${bound}:bound:`,
      };
    }
    return null;
  }

  /* As a validator the breach makes the form invalid, so it also stops Next
     and the tab bar - not only a toast that could be dismissed and ignored. */
  private rangeValidator(control: string): ValidatorFn {
    return (field: AbstractControl): ValidationErrors | null => {
      const breach = this.rangeBreach(field.value, this.termRange(control));
      return breach ? { range: breach } : null;
    };
  }

  /** each ranged term, with the unit (or type) whose change re-decides its range */
  private static readonly RANGED_TERMS: { [control: string]: string | null } = {
    principal: null,
    interestRate: 'interestRateUnit',
    loanPeriodLength: 'loanPeriodLengthUnit',
    loanRepaymentFrequency: 'loanRepaymentFrequencyPeriodUnit',
    gracePeriodLength: 'gracePeriodUnit',
    downPaymentPercentage: null,
    insurancePremium: 'insurancePremiumType',
  };

  /* The field turns red the moment its figure leaves the range, and the toast
     follows once typing pauses - so "1" on the way to "12" is not announced -
     and only once per breach, not on every further keystroke. */
  private watchTermRanges(): void {
    Object.entries(NewLoanAccountComponent.RANGED_TERMS).forEach(
      ([name, unit]) => {
        const control = this.basicForm.get(name);

        control.valueChanges
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(() => {
            if (control.hasError('range')) control.markAsTouched();
          });

        let announced = '';
        control.valueChanges
          .pipe(takeUntil(this.unsubscribe$), debounceTime(600))
          .subscribe(() => {
            const breach = control.getError('range');
            if (breach && breach.message !== announced) {
              this.notification.error(breach.message);
            }
            announced = breach?.message ?? '';
          });

        if (unit) {
          this.basicForm
            .get(unit)
            .valueChanges.pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => control.updateValueAndValidity());
        }
      },
    );
  }

  /** the short message under a field whose figure is out of range */
  public rangeHint(control: string): string {
    return this.basicForm?.get(control)?.getError('range')?.short ?? '';
  }

  /* The Basic tab's errors, named. A breached range is reported in its own
     words - the shared notifier knows only its own error keys and passes over
     this one in silence, which left Next looking dead. */
  private notifyBasicErrors(): void {
    this.basicForm.markAllAsTouched();
    const breach = Object.values(this.basicForm.controls)
      .map((control) => control.getError('range'))
      .find(Boolean);
    if (breach) {
      this.notification.error(breach.message);
      return;
    }
    this.validatorService.CheckMessagesAndNotify(this.basicForm.controls);
  }

  /* The tab bar marks the clicked tab active before asking whether it may be
     opened, so a refused move has to put the highlight back as well as the
     content - otherwise the next tab looked open over the Basic tab's form. */
  private stayOn(tab: string): void {
    this.toggleStates = this.toggleStates.map((state) => {
      state.isActive = state.value === tab;
      return state;
    });
    this.activeTab = tab;
  }

  /* The penalty's figures are panel values, not form controls, so their live
     check is worked out here and shown on the field in place of mat-error.
     The amount's range is in the product's basis and its grace range in the
     product's unit; a percentage basis also caps the amount at 100. */
  public get penaltyAmountError(): string {
    const penalty = this.productLatePayment;
    if (!this.isPanelSwitchOn('missedPaymentPenalty', penalty.required)) {
      return '';
    }
    const value = this.panelFieldValue(
      'missedPaymentPenaltyAmount',
      penalty.amount,
    );
    const basis = this.penaltyBasis;
    const percentage = !!basis && basis !== 'FLAT';
    const range =
      basis === penalty.type
        ? {
            label: '',
            min: penalty.minimumAmount,
            max: percentage
              ? Math.min(Number(penalty.maximumAmount ?? 100), 100)
              : penalty.maximumAmount,
            format: (figure: number) =>
              percentage ? `${figure}%` : this.moneyText(figure),
          }
        : percentage
          ? { label: '', min: null, max: 100, format: (figure: number) => `${figure}%` }
          : null;
    return this.rangeBreach(value, range)?.short ?? '';
  }

  public get penaltyGraceError(): string {
    const penalty = this.productLatePayment;
    if (!this.isPanelSwitchOn('missedPaymentPenalty', penalty.required)) {
      return '';
    }
    const unit = this.panelFieldValue(
      'missedPaymentPenaltyGracePeriodUnit',
      penalty.graceUnit,
    );
    if (unit !== penalty.graceUnit) return '';
    const value = this.panelFieldValue(
      'missedPaymentPenaltyGracePeriod',
      penalty.gracePeriod,
    );
    const label = this.periodUnitLabel(unit);
    return (
      this.rangeBreach(value, {
        label: '',
        min: penalty.minimumGracePeriod,
        max: penalty.maximumGracePeriod,
        format: (figure: number) =>
          `${figure} ${label}${figure === 1 ? '' : 's'}`,
      })?.short ?? ''
    );
  }

  /* The product's ranges, checked before leaving the Basic tab by either the
     Next button or the tab bar. */
  private termsWithinRange(): boolean {
    this.formValid = [];
    this.validateLoanSize();
    this.validateInterestRate();
    this.validateLoanFrequency();
    this.validateGracePeriod();
    this.validateLoanTerm();
    this.validateDownPayment();
    this.validateMissedPaymentPenalty();
    return !this.formValid.some((valid) => !valid);
  }

  /* The product's ranges are in the product's own units, so once a user who
     may change a unit has done so, that range no longer applies as it stands
     (2 - 6 months says nothing about years). The check is then left to the
     backend rather than raising a wrong error here. */
  private unitChanged(control: string): boolean {
    const product = this.loanProductRecords || {};
    const productUnit: { [control: string]: string } = {
      interestRateUnit:
        product.interestRatePeriodUnit ?? product.interestPeriodUnit,
      loanPeriodLengthUnit:
        product.loanPeriodUnit ?? product.loanPeriodLengthUnit,
      loanRepaymentFrequencyPeriodUnit:
        product.loanRepaymentFrequencyPeriodUnit,
      gracePeriodUnit: product.gracePeriodUnit,
    };
    const expected = productUnit[control];
    return !!expected && this.basicForm.get(control).value !== expected;
  }

  validateLoanSize() {
    // TEMPORARY ASSET-FINANCING UI PREVIEW OVERRIDE:
    // The hardcoded Figma values can exceed the selected product's configured
    // range. Skip this legacy range gate so QA can reach the remaining tabs.
    // Remove this branch when real asset-financing account values/API rules land.
    if (this.isAssetFinancing) {
      this.formValid.push(true);
      return;
    }

    // Grouped as typed ("20,000"), which Number() would read as NaN.
    const principal = this.toNumber(this.basicForm.get('principal').value);
    if (principal > Number(this.loanProductRecords.maximumPrincipal)) {
      this.notification.error(
        $localize`:@@loan_size_cannot_be_greater_than_the_maximum_loan_size:Loan Size cannot be greater than the maximum loan size`,
      );
      this.formValid.push(false);
      return;
    }

    if (Number(principal) < Number(this.loanProductRecords.minimumPrincipal)) {
      this.notification.error(
        $localize`:@@loan_size_cannot_be_less_than_the_minimum_loan_size:Loan Size cannot be less than the minimum loan size`,
      );
      this.formValid.push(false);
      return;
    }

    this.formValid.push(true);
  }

  validateLoanFrequency() {
    if (this.unitChanged('loanRepaymentFrequencyPeriodUnit')) {
      this.formValid.push(true);
      return;
    }

    const loanRepaymentFrequency = this.basicForm.get(
      'loanRepaymentFrequency',
    ).value;
    if (
      Number(loanRepaymentFrequency) >
      Number(this.loanProductRecords.maximumLoanRepaymentFrequency)
    ) {
      this.notification.error(
        $localize`:@@loan_frequency_cannot_be_greater_than_the_maximum_loan_frequency:Loan Frequency cannot be greater than the maximum loan frequency`,
      );
      this.formValid.push(false);
      return;
    }

    if (
      Number(loanRepaymentFrequency) <
      Number(this.loanProductRecords.minimumLoanRepaymentFrequency)
    ) {
      this.notification.error(
        $localize`:@@loan_frequency_cannot_be_less_than_the_minimum_loan_frequency:Loan Frequency cannot be less than the minimum loan frequency`,
      );
      this.formValid.push(false);
      return;
    }

    this.formValid.push(true);
  }

  validateGracePeriod() {
    if (this.unitChanged('gracePeriodUnit')) {
      this.formValid.push(true);
      return;
    }

    const gracePeriodLength = this.basicForm.get('gracePeriodLength').value;
    if (
      Number(gracePeriodLength) >
      Number(this.loanProductRecords.maximumGracePeriod)
    ) {
      this.notification.error(
        $localize`:@@grace_period_cannot_be_greater_than_the_maximum_grace_period:Grace period cannot be greater than the maximum Grace period`,
      );
      this.formValid.push(false);
      return;
    }

    if (
      Number(gracePeriodLength) <
      Number(this.loanProductRecords.minimumGracePeriod)
    ) {
      // Its own id: it shared the maximum's, so translated it read "greater than".
      this.notification.error(
        $localize`:@@grace_period_cannot_be_less_than_the_minimum_grace_period:Grace period cannot be less than the minimum Grace period`,
      );
      this.formValid.push(false);
      return;
    }

    this.formValid.push(true);
  }

  validateLoanTerm() {
    const loanPeriodLength = this.basicForm.get('loanPeriodLength').value;
    const loanRepaymentFrequency = this.basicForm.get(
      'loanRepaymentFrequency',
    ).value;
    const loanPeriodLengthUnit = this.basicForm.get(
      'loanPeriodLengthUnit',
    ).value;
    const loanRepaymentFrequencyPeriodUnit = this.basicForm.get(
      'loanRepaymentFrequencyPeriodUnit',
    ).value;
    // Only the range depends on the product's unit; the tenor/frequency
    // consistency check below converts units and always runs.
    const rangeApplies = !this.unitChanged('loanPeriodLengthUnit');
    if (
      rangeApplies &&
      Number(loanPeriodLength) >
        Number(this.loanProductRecords.maximumLoanPeriodLength)
    ) {
      this.notification.error(
        $localize`:@@loan_term_cannot_be_greater_than_the_maximum_loan_term:Loan Term cannot be greater than the maximum loan term`,
      );
      this.formValid.push(false);
      return;
    }

    if (
      rangeApplies &&
      Number(loanPeriodLength) <
        Number(this.loanProductRecords.minimumLoanPeriodLength)
    ) {
      this.notification.error(
        $localize`:@@loan_term_cannot_be_less_than_the_minimum_loan_term:Loan Term cannot be less than the minimum loan term`,
      );
      this.formValid.push(false);
      return;
    }

    this.formValid.push(true);

    switch (loanPeriodLengthUnit.toLowerCase()) {
      case 'day':
        {
          if (
            loanRepaymentFrequency &&
            loanPeriodLength % loanRepaymentFrequency !== 0
          ) {
            this.notification.error(
              $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
            );
            this.basicFormCtrl['loanPeriodLength'].setErrors({
              incorrect: true,
            });
            this.formValid.push(false);
          } else {
            this.basicFormCtrl['loanPeriodLength'].setErrors(null);
            this.formValid.push(true);
          }
        }
        break;
      case 'week':
        {
          switch (loanRepaymentFrequencyPeriodUnit.toLowerCase()) {
            case 'day':
              {
                if (
                  loanRepaymentFrequency &&
                  (loanPeriodLength * this.calendarDays.day.week) %
                    loanRepaymentFrequency !==
                    0
                ) {
                  this.notification.error(
                    $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
                  );
                  this.basicFormCtrl['loanPeriodLength'].setErrors({
                    incorrect: true,
                  });
                  this.formValid.push(false);
                } else {
                  this.basicFormCtrl['loanPeriodLength'].setErrors(null);
                  this.formValid.push(true);
                }
              }
              break;
            case 'week': {
              if (
                loanRepaymentFrequency &&
                loanPeriodLength % loanRepaymentFrequency !== 0
              ) {
                this.notification.error(
                  $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
                );
                this.basicFormCtrl['loanPeriodLength'].setErrors({
                  incorrect: true,
                });
                this.formValid.push(false);
              } else {
                this.basicFormCtrl['loanPeriodLength'].setErrors(null);
                this.formValid.push(true);
              }
            }
          }
        }
        break;
      case 'month':
        {
          switch (loanRepaymentFrequencyPeriodUnit.toLowerCase()) {
            case 'day':
              {
                if (
                  loanRepaymentFrequency &&
                  (loanPeriodLength * this.calendarDays.day.month) %
                    loanRepaymentFrequency !==
                    0
                ) {
                  this.notification.error(
                    $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
                  );
                  this.basicFormCtrl['loanPeriodLength'].setErrors({
                    incorrect: true,
                  });
                  this.formValid.push(false);
                } else {
                  this.basicFormCtrl['loanPeriodLength'].setErrors(null);
                  this.formValid.push(true);
                }
              }
              break;
            case 'week':
              {
                if (
                  loanRepaymentFrequency &&
                  (loanPeriodLength * this.calendarDays.week.month) %
                    loanRepaymentFrequency !==
                    0
                ) {
                  this.notification.error(
                    $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
                  );
                  this.basicFormCtrl['loanPeriodLength'].setErrors({
                    incorrect: true,
                  });
                  this.formValid.push(false);
                } else {
                  this.basicFormCtrl['loanPeriodLength'].setErrors(null);
                  this.formValid.push(true);
                }
              }
              break;
            case 'month': {
              if (
                loanRepaymentFrequency &&
                loanPeriodLength % loanRepaymentFrequency !== 0
              ) {
                this.notification.error(
                  $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
                );
                this.basicFormCtrl['loanPeriodLength'].setErrors({
                  incorrect: true,
                });
                this.formValid.push(false);
              } else {
                this.basicFormCtrl['loanPeriodLength'].setErrors(null);
                this.formValid.push(true);
              }
            }
          }
        }
        break;
      case 'year': {
        switch (loanRepaymentFrequencyPeriodUnit.toLowerCase()) {
          case 'day':
            {
              if (
                loanRepaymentFrequency &&
                (loanPeriodLength * this.calendarDays.day.year) %
                  loanRepaymentFrequency !==
                  0
              ) {
                this.notification.error(
                  $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
                );
                this.basicFormCtrl['loanPeriodLength'].setErrors({
                  incorrect: true,
                });
                this.formValid.push(false);
              } else {
                this.basicFormCtrl['loanPeriodLength'].setErrors(null);
                this.formValid.push(true);
              }
            }
            break;
          case 'week':
            {
              if (
                loanRepaymentFrequency &&
                (loanPeriodLength * this.calendarDays.week.year) %
                  loanRepaymentFrequency !==
                  0
              ) {
                this.notification.error(
                  $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
                );
                this.basicFormCtrl['loanPeriodLength'].setErrors({
                  incorrect: true,
                });
                this.formValid.push(false);
              } else {
                this.basicFormCtrl['loanPeriodLength'].setErrors(null);
                this.formValid.push(true);
              }
            }
            break;
          case 'month':
            {
              if (
                loanRepaymentFrequency &&
                (loanPeriodLength * this.calendarDays.month.year) %
                  loanRepaymentFrequency !==
                  0
              ) {
                this.notification.error(
                  $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
                );
                this.basicFormCtrl['loanPeriodLength'].setErrors({
                  incorrect: true,
                });
                this.formValid.push(false);
              } else {
                this.basicFormCtrl['loanPeriodLength'].setErrors(null);
                this.formValid.push(true);
              }
            }
            break;
          case 'year': {
            if (
              loanRepaymentFrequency &&
              loanPeriodLength % loanRepaymentFrequency !== 0
            ) {
              this.notification.error(
                $localize`:@@repayment_frequency_not_consistent_with_loan_tenor:Repayment frequency not consistent with loan tenor`,
              );
              this.basicFormCtrl['loanPeriodLength'].setErrors({
                incorrect: true,
              });
              this.formValid.push(false);
            } else {
              this.basicFormCtrl['loanPeriodLength'].setErrors(null);
              this.formValid.push(true);
            }
          }
        }
      }
    }
  }

  validateInterestRate() {
    if (this.unitChanged('interestRateUnit')) {
      this.formValid.push(true);
      return;
    }

    this.checkRange(
      this.basicForm.get('interestRate').value,
      this.loanProductRecords.minimumInterestRate,
      this.loanProductRecords.maximumInterestRate,
      $localize`:@@interest_rate_cannot_be_greater_than_the_maximum_interest_rate:Interest Rate cannot be greater than the maximum interest rate`,
      $localize`:@@interest_rate_cannot_be_less_than_the_minimum_interest_rate:Interest Rate cannot be less than the minimum interest rate`,
    );
  }

  /* Asset financing only. The product sets just a floor, and nobody puts down
     more than the whole asset. */
  validateDownPayment() {
    if (!this.isAssetFinancing) return;

    const product = this.loanProductRecords;
    this.checkRange(
      this.basicForm.get('downPaymentPercentage').value,
      product.minimumDownPaymentPercent ??
        product.minimumDownPayment ??
        product.minimumDownPaymentPercentage,
      100,
      $localize`:@@down_payment_cannot_be_more_than_the_asset_value:Down Payment cannot be more than 100% of the asset value`,
      $localize`:@@down_payment_cannot_be_less_than_the_minimum_down_payment:Down Payment cannot be less than the minimum down payment`,
    );
  }

  /* Only while the penalty is on: switched off, it posts no figures. The
     amount's range is in the units of the product's own basis, so it stops
     applying once the basis is changed - 500 - 20,000 means nothing for a
     percentage of the instalment. The grace range likewise, once its unit
     is changed (see unitChanged). */
  validateMissedPaymentPenalty() {
    const penalty = this.productLatePayment;
    if (!this.isPanelSwitchOn('missedPaymentPenalty', penalty.required)) return;

    const product = this.loanProductRecords;
    const basis = this.panelFieldValue(
      'missedPaymentPenaltyBasis',
      penalty.type,
    );
    // A percentage of the instalment cannot pass 100, whatever basis it came from.
    if (basis && basis !== 'FLAT') {
      this.checkRange(
        this.panelFieldValue('missedPaymentPenaltyAmount', penalty.amount),
        null,
        100,
        $localize`:@@penalty_percentage_above_100:Missed payment penalty cannot be more than 100% of the instalment`,
        '',
      );
    }
    if (basis === penalty.type) {
      this.checkRange(
        this.panelFieldValue('missedPaymentPenaltyAmount', penalty.amount),
        product.minimumLatePaymentPenaltyAmount,
        product.maximumLatePaymentPenaltyAmount,
        $localize`:@@penalty_amount_cannot_be_greater_than_the_maximum:Missed payment penalty cannot be greater than the maximum amount`,
        $localize`:@@penalty_amount_cannot_be_less_than_the_minimum:Missed payment penalty cannot be less than the minimum amount`,
      );
    }
    const graceUnit = this.panelFieldValue(
      'missedPaymentPenaltyGracePeriodUnit',
      penalty.graceUnit,
    );
    if (graceUnit === penalty.graceUnit) {
      this.checkRange(
        this.panelFieldValue(
          'missedPaymentPenaltyGracePeriod',
          penalty.gracePeriod,
        ),
        penalty.minimumGracePeriod,
        penalty.maximumGracePeriod,
        $localize`:@@penalty_grace_period_cannot_be_greater_than_the_maximum:Missed payment penalty grace period cannot be greater than the maximum grace period`,
        $localize`:@@penalty_grace_period_cannot_be_less_than_the_minimum:Missed payment penalty grace period cannot be less than the minimum grace period`,
      );
    }
  }

  /* Records its verdict on formValid like the checks above. A bound the
     product leaves unset does not restrict, rather than reading as 0. */
  private checkRange(
    value: any,
    min: any,
    max: any,
    tooHigh: string,
    tooLow: string,
  ): void {
    const figure = this.toNumber(value);
    const isSet = (bound: any) =>
      bound !== null && bound !== undefined && bound !== '';

    if (isSet(max) && figure > Number(max)) {
      this.notification.error(tooHigh);
      this.formValid.push(false);
    } else if (isSet(min) && figure < Number(min)) {
      this.notification.error(tooLow);
      this.formValid.push(false);
    } else {
      this.formValid.push(true);
    }
  }

  public saveFile(base64String: string, fileType: string) {
    if (!base64String) return;
    this.guarantorForm.get(fileType)?.setValue(base64String);
  }

  /**
   * The tenor checks stamp a manual { incorrect: true } error on loanPeriodLength.
   * Nothing else clears it, and the code that would (validateLoanFrequency and
   * friends) only runs *after* the basicForm.valid gate below — so the form stayed
   * invalid for good and Next aborted with no message at all. Re-running the
   * control's own validators drops the manual error and restores the real ones.
   */
  private clearStaleTenorError() {
    const loanPeriodLength = this.basicFormCtrl['loanPeriodLength'];
    if (loanPeriodLength?.hasError('incorrect')) {
      loanPeriodLength.updateValueAndValidity();
    }
  }

  public onSubmitBasic() {
    this.clearStaleTenorError();

    if (!this.basicForm.valid) {
      this.notifyBasicErrors();
      return;
    }

    if (!this.termsWithinRange()) return;

    if (this.loanProductRecords?.requireGuarantor) {
      this.navigateToState('guarantor');
    } else if (this.shouldShowDisbursementTab()) {
      this.navigateToState('disbursementDetails');
    } else {
      // MFI with third-party transfers off: no disbursement tab — disburse directly
      // without a savings/third-party target.
      this.submit();
    }
  }

  gotoBasic() {
    this.dialogRef.updateSize('860px');
    this.toggleStates.forEach((btnState: StateTypes) => {
      if (btnState.value === 'basic') {
        this.activeTab = 'basic';
        btnState.isActive = true;
      } else btnState.isActive = false;
    });
  }
  public onSubmitDisbursementDetails() {
    if (!this.disbursementForm.valid) {
      this.validatorService.CheckMessagesAndNotify(
        this.disbursementForm.controls,
      );
      return;
    }

    // Without one the loan is saved as "pay the vendor" with nobody to pay.
    if (this.isApprovedVendorDisbursement && !this.selectedVendor) {
      this.notification.error(
        $localize`:@@select_vendor_to_pay:Select the vendor/dealer to pay.`,
      );
      return;
    }

    // Additional validation based on transfer type
    if (this.selectedTransferType === 'SAVINGS_ACCOUNT') {
      const savingsAccountId =
        this.disbursementForm.get('savingsAccountId').value;
      // Only MFBs are required to pick a savings account; MFIs may disburse without one.
      if (this.isSavingsAccountRequired() && !savingsAccountId) {
        this.notification.error('Please select a savings account to credit.');
        return;
      }
    } else if (this.selectedTransferType === 'THIRD_PARTY') {
      const accountName = this.disbursementForm.get('accountName').value;
      const accountNumber = this.disbursementForm.get('accountNumber').value;
      const bankId = this.disbursementForm.get('bankId').value;

      if (!accountName || !accountNumber || !bankId) {
        this.notification.error('Please fill in all account details.');
        return;
      }

      // Only check for error if user modified the default values
      const isUsingDefaultAccount =
        this.defaultOwnAccount &&
        bankId === this.defaultOwnBankCode &&
        accountNumber === this.defaultOwnAccount.accountNumber;

      if (!isUsingDefaultAccount && this.errorMsg) {
        this.notification.error(
          'Please resolve account name enquiry errors before proceeding.',
        );
        return;
      }
    }

    // Submit the form
    this.submit();
  }
  // Add method to go back to disbursement details:
  gotoDisbursementDetails() {
    this.navigateToState('disbursementDetails');
  }

  onSubmitGuarantor() {
    if (this.identifierRequired && !this.guarantorForm.get('bvn')?.value) {
      this.notification.error(
        $localize`:@@identifier_required_for_guarantor:An identifier (${this.nationalIdentifiers.join('/')}) is required for this guarantor`,
      );
      return;
    }

    // When a guarantor's phone was prefilled from identity verification it may fail
    // ngx-intl-tel-input's format check (string vs phone object). Since the identity
    // service already validated the number, clear that specific error so it doesn't
    // block submission.
    const mobileCtrl = this.guarantorForm.get('mobileNumber');
    if (
      mobileCtrl?.errors?.['validatePhoneNumber'] &&
      (this.foundGuarantorId || this.hasVerifiedIdentity)
    ) {
      mobileCtrl.setErrors(null);
    }

    if (
      this.loanProductRecords?.requireGuarantor &&
      this.guarantorForm.invalid
    ) {
      const shouldLog = this.validatorService.CheckMessagesAndNotify(
        this.guarantorForm.controls,
      );
      if (shouldLog) return;
    }

    //
    if (this.shouldShowDisbursementTab()) {
      this.navigateToState('disbursementDetails');
    } else {
      // MFI with third-party transfers off: skip the (hidden) disbursement tab.
      this.submit();
    }
  }
  gotoGuarantor() {
    this.navigateToState('guarantor');
  }
  /**
   * The state dropdowns bind the country-data uuid, but the local-governments
   * endpoint keys off the stateCode. Translate via the cached raw list, falling
   * back to the value itself so a legacy stateCode still resolves.
   */
  private toStateCode(stateKey: any, rawStates: any[]): any {
    if (!stateKey) return stateKey;
    return rawStates?.find((state) => state.uuid === stateKey)?.stateCode ?? stateKey;
  }

  /**
   * setGuaratorRecords patches the state/LGA controls without emitting, so the
   * state valueChanges pipelines never load the LGA lists for a guarantor found by
   * phone or identifier — load them here so the saved LGAs have options to show.
   */
  private loadPrefilledGuarantorLgas(): void {
    const toOptions = (res: any) => {
      const list = res?.data?.localGovernments ?? [];
      return { raw: list, options: list.map((lga: any) => ({ label: lga.name, value: lga.uuid })) };
    };
    const stateKey = this.guarantorForm.get('stateId')?.value;
    if (stateKey && isNaN(Number(stateKey))) {
      this.countryService
        .getLocalGovernmentsByStateCode(this.toStateCode(stateKey, this.rawStates))
        .pipe(catchError(() => of(null)))
        .subscribe((res) => {
          const { raw, options } = toOptions(res);
          this.rawLocalGovernments = raw;
          this.localGovernments = options;
        });
    }
    const businessStateKey = this.guarantorForm.get('businessStateId')?.value;
    if (businessStateKey && isNaN(Number(businessStateKey))) {
      this.countryService
        .getLocalGovernmentsByStateCode(this.toStateCode(businessStateKey, this.rawBusinessStates))
        .pipe(catchError(() => of(null)))
        .subscribe((res) => {
          const { raw, options } = toOptions(res);
          this.rawBusinessLocalGovernments = raw;
          this.businessLocalGovernments = options;
        });
    }
  }

  /**
   * The guarantor form stores the country-data uuid for both the state and LGA
   * dropdowns, which is what the backend expects. This validates each value
   * against the cached raw lists, falling back to null if unresolved.
   */
  private resolveGuarantorLocationIds(payload: any): void {
    const stateUuid = payload.stateId;
    const businessStateUuid = payload.businessStateId;
    const lgaUuid = payload.lgaId;
    const businessLgaUuid = payload.businessLgaId;

    const matchedState = stateUuid
      ? this.rawStates.find((state) => state.uuid === stateUuid)
      : null;
    const matchedBusinessState = businessStateUuid
      ? this.rawBusinessStates.find((state) => state.uuid === businessStateUuid)
      : null;
    const matchedLga = lgaUuid
      ? this.rawLocalGovernments.find((lga) => lga.uuid === lgaUuid)
      : null;
    const matchedBusinessLga = businessLgaUuid
      ? this.rawBusinessLocalGovernments.find(
          (lga) => lga.uuid === businessLgaUuid,
        )
      : null;

    payload.stateId = matchedState?.uuid ?? null;
    payload.businessStateId = matchedBusinessState?.uuid ?? null;
    payload.lgaId = matchedLga?.uuid ?? null;
    payload.businessLgaId = matchedBusinessLga?.uuid ?? null;
    // Names/codes stored next to the uuids so the guarantor view renders them.
    payload.stateCode = matchedState?.stateCode ?? null;
    payload.stateName = matchedState?.stateName ?? null;
    payload.lgaName = matchedLga?.name ?? null;
    payload.businessStateCode = matchedBusinessState?.stateCode ?? null;
    payload.businessStateName = matchedBusinessState?.stateName ?? null;
    payload.businessLgaName = matchedBusinessLga?.name ?? null;
  }

  /* The product's late-payment settings under one set of names.
     The API spells them latePayment*; this screen's controls are named
     missedPayment*, and the read model still returns the pre-rename
     latePaymentPeriod* for the grace trio. Normalising here keeps that in one
     place instead of spread through the template and the payload builder. */
  public get productLatePayment(): {
    required: boolean;
    type: string;
    amount: any;
    minimumAmount: any;
    maximumAmount: any;
    gracePeriod: any;
    minimumGracePeriod: any;
    maximumGracePeriod: any;
    graceUnit: string;
  } {
    const product = this.loanProductRecords || {};
    return {
      required: !!product.requireLatePaymentPenalty,
      type: product.latePaymentPenaltyType || 'FLAT',
      amount: product.defaultLatePaymentPenaltyAmount ?? '',
      // A bound the product leaves unset does not restrict - no stand-in figures.
      minimumAmount: product.minimumLatePaymentPenaltyAmount ?? null,
      maximumAmount: product.maximumLatePaymentPenaltyAmount ?? null,
      gracePeriod:
        product.defaultLatePaymentGracePeriodLength ??
        product.latePaymentPeriodLength ??
        '',
      minimumGracePeriod:
        product.minimumLatePaymentGracePeriodLength ??
        product.minimumLatePaymentPeriodLength,
      maximumGracePeriod:
        product.maximumLatePaymentGracePeriodLength ??
        product.maximumLatePaymentPeriodLength,
      graceUnit:
        product.latePaymentGracePeriodUnit ||
        product.latePaymentPeriodUnit ||
        'DAY',
    };
  }

  /* Appends the unit the way the Coverage fields carry theirs, so a rate reads
     as "3%" rather than a bare 3. Guarded against double-appending in case the
     panel is ever made editable. */
  public withPercent(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const text = String(value);
    return text.endsWith('%') ? text : `${text}%`;
  }

  /** numeric value of a currency/percent field, or null when left blank */
  private amount(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
  }

  /* The three Other Loan Details panels below hold their state in the override
     maps rather than in form controls, so each one is read back out here. A
     switched-off panel sends its sub-fields as null rather than omitting them. */

  private latePaymentPanelPayload(): any {
    const product = this.productLatePayment;
    const enabled = this.isPanelSwitchOn(
      'missedPaymentPenalty',
      product.required,
    );

    if (!enabled) {
      return {
        requireLatePaymentPenalty: false,
        latePaymentPenaltyType: null,
        latePaymentPenaltyAmount: null,
        latePaymentGracePeriodLength: null,
        latePaymentGracePeriodUnit: null,
      };
    }

    return {
      requireLatePaymentPenalty: true,
      latePaymentPenaltyType: this.panelFieldValue(
        'missedPaymentPenaltyBasis',
        product.type,
      ),
      latePaymentPenaltyAmount: this.amount(
        this.panelFieldValue('missedPaymentPenaltyAmount', product.amount),
      ),
      latePaymentGracePeriodLength: this.amount(
        this.panelFieldValue(
          'missedPaymentPenaltyGracePeriod',
          product.gracePeriod,
        ),
      ),
      latePaymentGracePeriodUnit: this.panelFieldValue(
        'missedPaymentPenaltyGracePeriodUnit',
        product.graceUnit,
      ),
    };
  }

  private compulsorySavingsPanelPayload(): any {
    const product = this.loanProductRecords || {};

    if (!this.compulsorySavingsEnabled) {
      return {
        requireCompulsorySavings: false,
        compulsorySavingsType: null,
        compulsorySavingsAmount: null,
        savingsProductId: null,
        compulsorySavingsInterestRate: null,
        compulsorySavingsWithdrawable: false,
      };
    }

    return {
      requireCompulsorySavings: true,
      compulsorySavingsType: this.panelFieldValue(
        'compulsorySavingsType',
        product.compulsorySavingsType === 'NONE'
          ? 'FLAT'
          : product.compulsorySavingsType || 'FLAT',
      ),
      compulsorySavingsAmount: this.amount(
        this.panelFieldValue(
          'compulsorySavingsAmount',
          product.compulsorySavingsAmount ?? '',
        ),
      ),
      savingsProductId: product.savingsProductId ?? null,
      compulsorySavingsInterestRate: this.amount(
        this.panelFieldValue(
          'savingsInterestRate',
          product.compulsorySavingsInterestRate ??
            product.savingsProduct?.interestRate ??
            '',
        ),
      ),
      compulsorySavingsWithdrawable: this.isPanelSwitchOn(
        'earlyWithdrawal',
        !!product.compulsorySavingsWithdrawable,
      ),
    };
  }

  /* One row per requirement, each with its own switch and coverage field. The
     rows are keyed by guaranteeDetails' stable key, not the translated label. */
  private guaranteePanelPayload(): any {
    const fields: any = {
      requireGuarantor: false,
      guarantorMinimumCoveragePercent: null,
      requirePhysicalAsset: false,
      physicalAssetMinimumCoveragePercent: null,
      requireFixedDepositLien: false,
      fixedDepositLienMinimumCoveragePercent: null,
    };

    const coverageField: { [key: string]: string } = {
      guarantor: 'guarantorMinimumCoveragePercent',
      physicalAsset: 'physicalAssetMinimumCoveragePercent',
      fixedDepositLien: 'fixedDepositLienMinimumCoveragePercent',
    };
    const requireField: { [key: string]: string } = {
      guarantor: 'requireGuarantor',
      physicalAsset: 'requirePhysicalAsset',
      fixedDepositLien: 'requireFixedDepositLien',
    };

    this.guaranteeDetails.forEach((item) => {
      const enabled = this.isPanelSwitchOn(
        `guarantee:${item.key}`,
        item.enabled,
      );
      fields[requireField[item.key]] = enabled;
      // The coverage shown is a display string ("100%"), so strip it back.
      fields[coverageField[item.key]] = enabled
        ? this.amount(
            this.panelFieldValue(`coverage:${item.key}`, item.coverage),
          )
        : null;
    });

    return fields;
  }

  submit() {
    const clientType = this.isCorporate ? 'CORPORATE' : 'INDIVIDUAL';

    this.clearStaleTenorError();

    if (!this.basicForm.valid) {
      this.validatorService.CheckMessagesAndNotify(this.basicForm.controls);
      return;
    }

    if (!this.disbursementForm.valid) {
      this.validatorService.CheckMessagesAndNotify(
        this.disbursementForm.controls,
      );
      return;
    }

    let guarantorPayload = null;
    if (this.loanProductRecords?.requireGuarantor) {
      if (this.foundGuarantorId) {
        // Existing guarantor — always send the ID; include any edits the user made
        const rawValues = this.guarantorForm.getRawValue();
        const bvn = rawValues['bvn'];
        // Use the stored actual type from phone search; fall back to the form value
        const identifierType =
          this.guarantorPhoneSearchIdentifierType ||
          rawValues['identifierType'];
        const mobile = rawValues['mobileNumber'];
        const altMobile = rawValues['alternatePhoneNumber'];
        guarantorPayload = {
          ...rawValues,
          id: this.foundGuarantorId,
          passportPhoto: rawValues['photo'],
          identifier: bvn || null,
          identifierType: identifierType || null,
          identifierVerified:
            this.foundGuarantorIdentifierVerified || this.hasVerifiedIdentity,
          mobileNumber:
            typeof mobile === 'string' ? mobile : (mobile?.e164Number ?? null),
          alternatePhoneNumber: altMobile
            ? typeof altMobile === 'string'
              ? altMobile
              : (altMobile?.e164Number ?? null)
            : null,
        };
        delete guarantorPayload['photo'];
        delete guarantorPayload['bvn'];
        delete guarantorPayload['defaultPhoneSlot'];
      } else {
        // New guarantor — full payload
        guarantorPayload = this.guarantorForm.getRawValue();
        guarantorPayload['passportPhoto'] = guarantorPayload['photo'];
        delete guarantorPayload['photo'];
        guarantorPayload['identifier'] = guarantorPayload['bvn'] || null;
        delete guarantorPayload['bvn'];
        guarantorPayload['identifierVerified'] = this.hasVerifiedIdentity;
        // Typed by hand this control holds an ngx-intl-tel-input object; patched from
        // the API it is a plain string; after guarantorForm.reset() it is null.
        const mobile = guarantorPayload['mobileNumber'];
        guarantorPayload['mobileNumber'] =
          typeof mobile === 'string' ? mobile : (mobile?.e164Number ?? null);
        const altMobile = guarantorPayload['alternatePhoneNumber'];
        guarantorPayload['alternatePhoneNumber'] = altMobile
          ? typeof altMobile === 'string'
            ? altMobile
            : (altMobile?.e164Number ?? null)
          : null;
        delete guarantorPayload['defaultPhoneSlot'];
      }

      // Say what is wrong with the guarantor's phone here, instead of letting the server answer
      // "Mobile number is required" twice. An alternate number alone is fine: the backend uses it
      // as the mobile number when the mobile is blank, so nothing the mobile field holds (a stray
      // space, a half-typed number) may block it. See guarantorPhoneProblem.
      if (guarantorPayload) {
        const problem = guarantorPhoneProblem(
          this.guarantorForm.getRawValue()['mobileNumber'],
          guarantorPayload['mobileNumber'],
          guarantorPayload['alternatePhoneNumber'],
        );
        if (problem === 'incomplete') {
          this.notification.error(
            $localize`:@@guarantor_mobile_incomplete:The guarantor's mobile number is not complete. Enter the full number after the country code, without the leading 0.`,
          );
          return;
        }
        if (problem === 'required') {
          this.notification.error(
            $localize`:@@guarantor_phone_required:Enter the guarantor's mobile number, or an alternate phone number.`,
          );
          return;
        }
        // Nothing usable in the mobile field: send it blank so the backend takes the alternate number
        if (!phoneDigits(guarantorPayload['mobileNumber'])) {
          guarantorPayload['mobileNumber'] = null;
        }
        // The guarantor has no "default phone" field, so the default is expressed by order: the number marked
        // as default is sent as the mobile number, which is what every screen, search and message shows.
        const ordered = applyDefaultPhone(
          guarantorPayload['mobileNumber'],
          guarantorPayload['alternatePhoneNumber'],
          this.guarantorForm.get('defaultPhoneSlot')?.value,
        );
        guarantorPayload['mobileNumber'] = ordered.mobileNumber;
        guarantorPayload['alternatePhoneNumber'] = ordered.alternatePhoneNumber;
      }

      // Resolve stateCode/lgaUuid → numeric Long IDs expected by the backend
      // and add country fields to the payload
      if (guarantorPayload) {
        this.resolveGuarantorLocationIds(guarantorPayload);
        guarantorPayload['country'] = guarantorPayload['countryId'] ?? null;
        guarantorPayload['businessCountry'] =
          guarantorPayload['businessCountryId'] ?? null;
        delete guarantorPayload['countryId'];
        delete guarantorPayload['businessCountryId'];
        // The form's countryId control holds the code; send the real uuid + name too.
        const country = this.rawCountries?.find(
          (c: any) => c.countryCode === guarantorPayload['country'],
        );
        if (country) {
          guarantorPayload['countryId'] = country.uuid ?? null;
          guarantorPayload['countryName'] = country.countryName ?? null;
        }
      }
    }

    const disbursementData = this.disbursementForm.getRawValue();

    // Build disbursement payload based on transfer type
    let disbursementPayload: any = {};

    if (disbursementData.transferType === 'SAVINGS_ACCOUNT') {
      // For Savings Account. MFIs may disburse without a savings target — when no
      // account is selected, send no disbursement destination at all.
      if (disbursementData.savingsAccountId) {
        /* savingsAccountId is the field the backend reads for an internal
           transfer - what dev has always sent here. Sent as ownAccountId
           alone, the loan was saved with no destination, fell back to a
           third-party transfer at disbursement and failed for want of a
           beneficiary. ownAccountId stays alongside until the backend
           confirms which it reads; an unknown field is ignored. */
        disbursementPayload = {
          transferType: 'INTERNAL_TRANSFER',
          savingsAccountId: disbursementData.savingsAccountId,
          ownAccountId: disbursementData.savingsAccountId,
        };
      } else {
        disbursementPayload = {};
      }
    } else if (disbursementData.transferType === 'THIRD_PARTY') {
      // For Third Party Account (includes own account functionality)
      disbursementPayload = {
        transferType: 'THIRD_PARTY',
        ownAccountId: null,
        // The control holds the bank code as a string ("090270"); the API
        // takes it numerically.
        bankId: this.amount(disbursementData.bankId),
        bankName: this.selectedDisbursementBankName,
        accountName: disbursementData.accountName,
        accountNumber: disbursementData.accountNumber,
        accountType: 'SAVINGS',
      };
    }

    // Paid out in the loan's own currency, which is its product's.
    disbursementPayload.currency = this.loanCurrency;
    disbursementPayload.narration = this.disbursementNarration;

    // Repayment destination is independent of the disbursement branch above —
    // it is where repayments land, not where the loan is paid out.
    if (disbursementData.repaymentAccountId) {
      disbursementPayload.repaymentGlId = disbursementData.repaymentAccountId;
    }

    const basic = this.basicForm.getRawValue();

    /* Built field by field rather than spreading the form, because the control
       names and the API's names differ in several places (purpose, gracePeriod,
       the insurance pair) and a spread would post the control name instead. */
    const payload: any = {
      clientId: basic.clientId,
      loanAccountType: clientType,
      loanProductId: basic.loanProductId,
      purpose: basic.description,
      principal: this.amount(basic.principal),

      // Asset financing only.
      assetValue: this.isAssetFinancing ? this.amount(basic.assetValue) : null,
      insuranceAnnualPremiumType: this.isAssetFinancing
        ? basic.insurancePremiumType
        : null,
      insuranceAnnualPremiumAmount: this.isAssetFinancing
        ? this.amount(basic.insurancePremium)
        : null,
      downPaymentPercent: this.isAssetFinancing
        ? this.amount(basic.downPaymentPercentage)
        : null,

      interestRate: this.amount(basic.interestRate),
      interestRateUnit: basic.interestRateUnit,

      loanPeriodLength: this.amount(basic.loanPeriodLength),
      loanPeriodLengthUnit: basic.loanPeriodLengthUnit,

      loanRepaymentFrequency: this.amount(basic.loanRepaymentFrequency),
      loanRepaymentFrequencyPeriodUnit: basic.loanRepaymentFrequencyPeriodUnit,

      gracePeriod: this.amount(basic.gracePeriodLength),
      loanRepaymentGracePeriodUnit: basic.gracePeriodUnit,

      amortizationType: basic.amortizationType,
      balloonPaymentPercent:
        basic.amortizationType === 'BALLOON'
          ? this.amount(this.loanProductRecords?.defaultBalloonPaymentPercent)
          : null,

      ...this.latePaymentPanelPayload(),
      ...this.compulsorySavingsPanelPayload(),
      ...this.guaranteePanelPayload(),

      ...disbursementPayload,
      fundSourceAccountId: basic.fundSourceAccountId || null,

      /* Only asset financing shows the method select. A normal loan always
         goes to the borrower; sending the select's default instead saved it
         as "pay the vendor" with no vendor, and disbursement then failed for
         want of a beneficiary. */
      disbursementMethod: this.isAssetFinancing
        ? this.disbursementForm.get('disbursementMethod').value
        : 'DIRECT_TO_BORROWER',
      vendorId: this.isApprovedVendorDisbursement
        ? this.selectedVendorId
        : null,

      // No control for a future start date yet; the API defaults it.
      startDate: null,
      requestSource: 'WEBCLIENT',

      guarantorRequest: {
        ...guarantorPayload,
      },
    };

    // Date of birth is optional and stays '' when identity verification returns none.
    // formatDate throws on '' (and turns null into today), which killed Submit before
    // the request was sent — so only format a date that is actually there.
    if (this.loanProductRecords?.requireGuarantor) {
      const dateOfBirth = this.guarantorForm.getRawValue().dateOfBirth;
      payload.guarantorRequest.dateOfBirth = dateOfBirth
        ? this.utils.formatDate_custom_yearFirst(dateOfBirth)
        : null;
    }

    this.loanAccountService.createLoanAccount(payload).subscribe({
      next: (res: any) => {
        if (!res.successful) return this.notification.error(res.message);
        this.notification.success(
          $localize`:@@loan_account_created_successfully:Loan account created successfully`,
        );
        this.dialogRef.close({ successful: true, loanAccountId: res.data.id });
      },
      // The interceptor already toasts every other status (including business-rule
      // rejections like "guarantor is guaranteeing an active loan") — showing it
      // again here duplicated that message. It only stays silent on 504/0, so this
      // only needs to cover those or Submit looks dead when the request fails.
      error: (err) => {
        if (err?.status === 504 || err?.status === 0) {
          this.notification.error(
            'Could not create the loan account. Please check your connection and try again.',
          );
        }
      },
    });
  }

  public onCompInit() {
    this.initForm();
    // this.setupAccountInputDebounce();
    this.guarantorForm?.get('identifierType')?.setValue('Phone');
    this.loadOrganization();
    this.loadOwnAccounts();

    this.countryService.getCountries().subscribe({
      next: (res) => {
        const list = res?.data?.data ?? res?.data ?? res ?? [];
        this.rawCountries = list;
        // Map countryName → label, countryCode → value (countryCode is what the states endpoint needs)
        this.countries = list.map((country: any) => ({
          label: country.countryName,
          value: country.countryCode,
        }));
      },
    });

    // Reload residential states when country changes (value is countryCode e.g. "NGA")
    this.guarantorForm
      .get('countryId')
      .valueChanges.pipe(
        filter((code) => !!code),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
        switchMap((code) => this.countryService.getStatesByCountryCode(code)),
      )
      .subscribe((res) => {
        const stateList =
          res?.states ??
          res?.data?.states ??
          res?.data?.data ??
          res?.data ??
          [];
        this.rawStates = stateList;
        this.states = stateList.map((state: any) => ({
          label: state.stateName,
          value: state.uuid,
        }));
        const selectedCountry = this.rawCountries.find(
          (country) =>
            country.countryCode === this.guarantorForm.get('countryId')?.value,
        );
        this.residentialStateLabel =
          selectedCountry?.subNationalLabel || 'State';
        this.residentialLgaLabel =
          selectedCountry?.localLevelLabel || 'Local Govt.';
        this.localGovernments = [];
        this.rawLocalGovernments = [];
        this.guarantorForm.get('stateId')?.setValue('', { emitEvent: true });
        this.guarantorForm.get('lgaId')?.setValue('', { emitEvent: false });
      });

    // Reload business states when country changes
    this.guarantorForm
      .get('businessCountryId')
      .valueChanges.pipe(
        filter((code) => !!code),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
        switchMap((code) => this.countryService.getStatesByCountryCode(code)),
      )
      .subscribe((res) => {
        const stateList =
          res?.states ??
          res?.data?.states ??
          res?.data?.data ??
          res?.data ??
          [];
        this.rawBusinessStates = stateList;
        this.businessStates = stateList.map((state: any) => ({
          label: state.stateName,
          value: state.uuid,
        }));
        const selectedCountry = this.rawCountries.find(
          (country) =>
            country.countryCode ===
            this.guarantorForm.get('businessCountryId')?.value,
        );
        this.businessStateLabel = selectedCountry?.subNationalLabel || 'State';
        this.businessLgaLabel =
          selectedCountry?.localLevelLabel || 'Local Govt.';
        this.businessLocalGovernments = [];
        this.rawBusinessLocalGovernments = [];
        this.guarantorForm
          .get('businessStateId')
          ?.setValue('', { emitEvent: true });
        this.guarantorForm
          .get('businessLgaId')
          ?.setValue('', { emitEvent: false });
      });

    // console.log(this.organizationName);
    // console.log(this.data);

    // Meant to get all loan products (first 500)
    const pageRequest: PageRequest = {
      page: 0,
      pageSize: 500,
      state: 'APPROVED',
      sortField: '',
      sortOrder: 'DESC',
      filter: '',
    };
    const request = `page=${pageRequest.page}&pageSize=${pageRequest.pageSize}&status=${pageRequest.state}&sort-field=${pageRequest.sortField}&sort-order=${pageRequest.sortOrder}&filter=${pageRequest.filter}`;
    this.loanProductService.getLoanProducts(request).subscribe((res) => {
      if (!res.successful) return;
      this.loanProductRecordsList = res.data || [];
      this.updateLoanProductOptions();
    });
  }

  /* The client pages used to fetch the tenant profile, a bank list and the
     client's savings accounts before opening this modal, so it took as long
     as the slowest of them to appear. It now opens straight away and these
     arrive in the background (banks already come from loadBanks). */
  private loadOrganization(): void {
    if (this.organization) {
      this.applyOrganization(this.organization);
      return;
    }
    this.dataService
      .getOrganization()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => this.applyOrganization(res?.data ?? res),
        error: () => {},
      });
  }

  private applyOrganization(organization: any): void {
    if (!organization) return;
    this.organization = organization;
    this.organizationName =
      organization?.tenant?.name || this.organizationName;
    this.nationalIdentifiers = nationalIdentifierOptions(
      organization?.tenantCountry,
    );
    this.identityVerificationEnabled =
      organization?.tenant?.enableIdentityVerification ?? true;
    this.identifierTypeOptions = this.identityVerificationEnabled
      ? ['Phone', ...this.nationalIdentifiers]
      : ['Phone'];
    if (this.identityVerificationEnabled) {
      const bioFields = [
        'firstName',
        'lastName',
        'middleName',
        'gender',
        'dateOfBirth',
        'email',
        'mobileNumber',
      ];
      bioFields.forEach((field) =>
        this.guarantorForm.get(field)?.disable({ emitEvent: false }),
      );
    }

    const tenantCountryCode = organization?.tenantCountry?.countryCode;
    if (tenantCountryCode) {
      this.countryService.getByCountryCode(tenantCountryCode).subscribe({
        next: (res) => {
          const country = res?.data ?? res;
          this.tenantCountryName = country?.countryName || tenantCountryCode;
          // Default both country dropdowns to the tenant's country (value = countryCode e.g. "NGA")
          // emitEvent: true so the valueChanges pipeline fires and loads states immediately
          const code = country?.countryCode ?? tenantCountryCode;
          this.tenantCountryCode = code;
          this.guarantorForm.get('countryId')?.setValue(code);
          this.guarantorForm.get('businessCountryId')?.setValue(code);
        },
      });
    }
  }

  private loadOwnAccounts(): void {
    if (this.data.savingsAccount) return;
    const type =
      this.data.savingsAccountType ??
      (this.isCorporate ? 'CORPORATE' : 'INDIVIDUAL');
    this.clientService
      .getSavingsAccount(this.clientId, type)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => this.applyOwnAccounts(res?.data ?? []),
        error: () => {},
      });
  }

  /* The Tenant transfer credits the client's first savings account, the same
     default the form would have opened with had the accounts been there. */
  private applyOwnAccounts(accounts: any[]): void {
    this.ownAccount = Array.isArray(accounts) ? accounts : [];
    const control = this.disbursementForm?.get('savingsAccountId');
    if (control && !control.value && this.ownAccount[0]?.id) {
      control.setValue(this.ownAccount[0].id, { emitEvent: false });
    }
  }

  ngOnInit(): void {
    this.clientSetup.initializeCurrencyFormatter().subscribe();
    this.loadTenantFlags();
    this.loadBanks();
    this.loadVendorDealers();
    this.onCompInit();
  }

  ngAfterViewInit(): void {
    // Start-up requests are under way; the scope ends when they finish.
    this.quietScope.settle();
  }

  override ngOnDestroy(): void {
    this.quietScope.close();
    super.ngOnDestroy();
  }

  private loadVendorDealers(): void {
    this.vendorService
      // Only an approved vendor can be disbursed to, the same way this modal
      // only lists APPROVED loan products.
      .getVendors(
        'page=0&pageSize=500&state=APPROVED&sort-field=&sort-order=DESC&filter=',
      )
      .subscribe({
        next: (res: any) => {
          this.vendorDealers = Array.isArray(res?.data)
            ? res.data
            : (res?.data?.content ?? []);
        },
        error: () => (this.vendorDealers = []),
      });
  }

  byLoanId(_index: number, loan: any) {
    return loan.id;
  }
}
